import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DynamicBlockRenderer from '../../components/editor/DynamicBlockRenderer';
import ExportButtons from '../../components/export/ExportButtons';
import AIChat, { DRAWER_WIDTH } from '../../components/chat/AIChat';
import { useSeance } from '../../hooks';
import api, { musicApi, galleryImageApi } from '../../services/api';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Fab,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Menu,
  ListItemIcon,
  ListItemText,
  Grid,
  Checkbox,
  CardMedia
} from '@mui/material';
import {
  ArrowBack,
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircle,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  Notes as NotesIcon,
  Quiz as QuizIcon,
  Poll as PollIcon,
  Chat as ChatIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  SmartToy as SmartToyIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as CloudUploadIcon,
  ContentCopy as ContentCopyIcon,
  Translate as TranslateIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  TableChart as CsvIcon,
  Slideshow as SlideshowIcon,
  Description as DescriptionIcon,
  MusicNote as MusicNoteIcon
} from '@mui/icons-material';

function SeanceDetailsScreen() {
  const { seanceId } = useParams();
  const navigate = useNavigate();
  const { seance: initialSeance, isLoading, updateSeance } = useSeance(seanceId);
  const [seance, setSeance] = useState(null);
  const [activeSequenceIndex, setActiveSequenceIndex] = useState(0);
  const [blocMenuAnchor, setBlocMenuAnchor] = useState(null);
  const [blocInsertIndex, setBlocInsertIndex] = useState(null);
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [newSequenceTitle, setNewSequenceTitle] = useState('');
  const [editingSequenceIndex, setEditingSequenceIndex] = useState(null);
  const [editingSequenceTitle, setEditingSequenceTitle] = useState('');
  const [sequenceCreationMode, setSequenceCreationMode] = useState('prompt'); // 'prompt' or 'questionnaire'
  const [sequenceFormData, setSequenceFormData] = useState({
    title: '',
    prompt: '',
    subject: '',
    sub_subject: '',
    age: '',
    level: '',
    language: '',
    duration: 15,
    objectives: '',
    materials: ''
  });
  const [movingBlockId, setMovingBlockId] = useState(null);
  const [regenerateSequenceDialogOpen, setRegenerateSequenceDialogOpen] = useState(false);
  const [regenerateSequenceIndex, setRegenerateSequenceIndex] = useState(null);
  const [regenerateSequencePrompt, setRegenerateSequencePrompt] = useState('');
  const [editSequenceTitle, setEditSequenceTitle] = useState('');
  const [editSequenceDialogOpen, setEditSequenceDialogOpen] = useState(false);
  const [editSequencePrompt, setEditSequencePrompt] = useState('');
  const [newlyAddedBlocId, setNewlyAddedBlocId] = useState(null);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [seancePrompt, setSeancePrompt] = useState('');
  const [aiChatDialogOpen, setAiChatDialogOpen] = useState(false);
  const [isRegeneratingSeance, setIsRegeneratingSeance] = useState(false);
  const [isSavingBlock, setIsSavingBlock] = useState(false);
  const [regenerationError, setRegenerationError] = useState(null);
  const [regenerationTimeoutId, setRegenerationTimeoutId] = useState(null);
  
  // Block actions menu state
  const [blockActionsMenuAnchor, setBlockActionsMenuAnchor] = useState(null);
  const [blockActionsMenuBlockIndex, setBlockActionsMenuBlockIndex] = useState(null);
  
  // Image bank dialog state
  const [imageBankDialogOpen, setImageBankDialogOpen] = useState(false);
  const [imageBankBlockIndex, setImageBankBlockIndex] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedGalleryImages, setSelectedGalleryImages] = useState(new Set());
  const [loadingGalleryImages, setLoadingGalleryImages] = useState(false);
  
  // Sequence actions menu state
  const [sequenceActionsMenuAnchor, setSequenceActionsMenuAnchor] = useState(null);
  const [sequenceActionsMenuIndex, setSequenceActionsMenuIndex] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingBlockIndex, setUploadingBlockIndex] = useState(null);
  const [uploadErrorDialogOpen, setUploadErrorDialogOpen] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [translateMenuAnchor, setTranslateMenuAnchor] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatingLanguage, setTranslatingLanguage] = useState(null);
  const [infoMenuAnchor, setInfoMenuAnchor] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const infoMenuButtonRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // AI bloc modification state
  const [aiBlocDialogOpen, setAiBlocDialogOpen] = useState(false);
  const [aiBlocPrompt, setAiBlocPrompt] = useState('');
  const [aiBlocIndex, setAiBlocIndex] = useState(null);
  const [isModifyingBloc, setIsModifyingBloc] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Image generation state
  const [imageGenerationDialogOpen, setImageGenerationDialogOpen] = useState(false);
  const [imageGenerationBlockId, setImageGenerationBlockId] = useState(null);
  const [imageGenerationPrompt, setImageGenerationPrompt] = useState('');
  const [imageGenerationNbVariations, setImageGenerationNbVariations] = useState(2);
  const [imageGenerationStyle, setImageGenerationStyle] = useState('cartoon');
  const [imageGenerationAspectRatio, setImageGenerationAspectRatio] = useState('16:9');
  const [imageGenerationAiChatOpen, setImageGenerationAiChatOpen] = useState(true);
  const [imageGenerationModels, setImageGenerationModels] = useState([]);
  const [selectedImageGenerationModel, setSelectedImageGenerationModel] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  
  // Image generation for sequence (when adding image block)
  const [sequenceImageGenerationDialogOpen, setSequenceImageGenerationDialogOpen] = useState(false);
  const [sequenceImageGenerationPrompt, setSequenceImageGenerationPrompt] = useState('');
  const [sequenceImageGenerationNbVariations, setSequenceImageGenerationNbVariations] = useState(2);
  const [sequenceImageGenerationStyle, setSequenceImageGenerationStyle] = useState('cartoon');
  const [sequenceImageGenerationAspectRatio, setSequenceImageGenerationAspectRatio] = useState('16:9');
  const [selectedSequenceImageGenerationModel, setSelectedSequenceImageGenerationModel] = useState('');
  const [isGeneratingSequenceImages, setIsGeneratingSequenceImages] = useState(false);
  
  // State to trigger opening regenerate dialog after image generation
  const [openRegenerateDialogForBlock, setOpenRegenerateDialogForBlock] = useState(null);
  
  // Delete seance confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Delete sequence confirmation dialog state
  const [deleteSequenceDialogOpen, setDeleteSequenceDialogOpen] = useState(false);
  
  // Delete sequence confirmation dialog state
  const [sequenceToDelete, setSequenceToDelete] = useState(null);
  const [isDeletingSequence, setIsDeletingSequence] = useState(false);
  
  // Delete bloc confirmation dialog state
  const [deleteBlocDialogOpen, setDeleteBlocDialogOpen] = useState(false);
  const [blocToDelete, setBlocToDelete] = useState({ sequenceIndex: null, blocIndex: null });
  const [isDeletingBloc, setIsDeletingBloc] = useState(false);
  
  // Drag and drop state for sequences
  const [draggedSequenceIndex, setDraggedSequenceIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isProcessingSequenceDrop, setIsProcessingSequenceDrop] = useState(false);
  
  // Thumbnail selection state
  const [thumbnailDialogOpen, setThumbnailDialogOpen] = useState(false);
  const [allSeanceImages, setAllSeanceImages] = useState([]);
  const [isSettingThumbnail, setIsSettingThumbnail] = useState(false);
  
  // Music selection state
  const [musicSelectionDialogOpen, setMusicSelectionDialogOpen] = useState(false);
  const [availableMusics, setAvailableMusics] = useState([]);
  const [musicsLoading, setMusicsLoading] = useState(false);
  const [pendingMusicBlocInsertIndex, setPendingMusicBlocInsertIndex] = useState(null);
  
  // Available bloc types with their metadata
  const availableBlocTypes = [
    {
      type: 'image',
      label: 'Image',
      description: 'Ajouter une image ou une galerie d\'images',
      icon: <ImageIcon />,
      color: '#1976d2'
    },
    {
      type: 'teacher_notes',
      label: 'Notes du professeur',
      description: 'Contenu pédagogique et explications',
      icon: <NotesIcon />,
      color: '#388e3c'
    },
    {
      type: 'quiz',
      label: 'Quiz',
      description: 'Questionnaire interactif avec réponses',
      icon: <QuizIcon />,
      color: '#F57C00'
    },
    {
      type: 'sondage',
      label: 'Sondage',
      description: 'Questionnaire sans bonnes/mauvaises réponses',
      icon: <PollIcon />,
      color: '#9C27B0'
    },
    {
      type: 'music',
      label: 'Musique',
      description: 'Ajouter une musique ou un fichier audio',
      icon: <MusicNoteIcon />,
      color: '#4A90E2'
    }
  ];
  
  // Update local seance state when initialSeance changes
  useEffect(() => {
    if (initialSeance) {
      setSeance(initialSeance);
    }
  }, [initialSeance]);

  // Fetch musics when component mounts
  useEffect(() => {
    const fetchMusics = async () => {
      try {
        setMusicsLoading(true);
        const response = await musicApi.getAllMusics();
        if (response.success && response.data && response.data.musics) {
          setAvailableMusics(response.data.musics);
        } else {
          setAvailableMusics([]);
        }
      } catch (err) {
        console.error('Error fetching musics:', err);
        setAvailableMusics([]);
      } finally {
        setMusicsLoading(false);
      }
    };
    
    fetchMusics();
  }, []);

  // Debug: Log when thumbnail dialog opens and re-collect images
  useEffect(() => {
    if (thumbnailDialogOpen && seance) {
      console.log('Thumbnail dialog opened, current allSeanceImages:', allSeanceImages.length);
      // Re-collect images when dialog opens to ensure we have latest data
      const images = collectAllSeanceImages();
      console.log('Re-collected images on dialog open:', images.length);
      if (images.length > 0 && images.length !== allSeanceImages.length) {
        console.log('Image count changed, updating state:', images.length, 'vs', allSeanceImages.length);
        setAllSeanceImages(images);
      } else if (images.length > 0 && allSeanceImages.length === 0) {
        // If we have images but state is empty, update it
        console.log('Setting images for first time:', images.length);
        setAllSeanceImages(images);
      }
    }
  }, [thumbnailDialogOpen, seance]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (regenerationTimeoutId) {
        clearTimeout(regenerationTimeoutId);
      }
    };
  }, [regenerationTimeoutId]);

  // Fetch AI models for image generation
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        const models = await api.aiModels.getAIModels('image_generation_models');
        
        // Ensure models is always an array
        const modelsArray = Array.isArray(models) ? models : [];
        setImageGenerationModels(modelsArray);
        
        // Set the first model as default if available
        if (modelsArray.length > 0) {
          setSelectedImageGenerationModel(modelsArray[0].id);
          setSelectedSequenceImageGenerationModel(modelsArray[0].id);
        }
      } catch (err) {
        console.error('Error fetching AI models:', err);
        setImageGenerationModels([]); // Set empty array on error
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);
  
  // Note: WebSocket removed as backend no longer uses it

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'draft': return 'default';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon />;
      case 'in_progress': return <PlayIcon />;
      default: return <SchoolIcon />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Terminée';
      case 'in_progress': return 'En cours';
      case 'draft': return 'Brouillon';
      case 'failed': return 'Échec';
      default: return 'Inconnu';
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 'easy': return 'Débutant';
      case 'intermediate': return 'Intermédiaire';
      case 'advanced': return 'Avancé';
      default: return level || 'Non spécifié';
    }
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2, color: '#3498DB' }} />
              <Typography variant="h4" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                Chargement de la séance...
              </Typography>
              <Typography variant="h6" sx={{ color: '#7F8C8D' }}>
                Veuillez patienter pendant que nous récupérons les détails de la séance.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </div>
    );
  }

  if (!seance) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                Séance non trouvée
              </Typography>
              <Typography variant="h6" sx={{ color: '#7F8C8D', mb: 3 }}>
                La séance demandée n'existe pas ou a été supprimée.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/seance')}
                sx={{
                  bgcolor: '#3498DB',
                  '&:hover': { bgcolor: '#2980B9' }
                }}
              >
                Retour aux séances
              </Button>
            </CardContent>
          </Card>
        </Container>
      </div>
    );
  }

  const activeSequence = seance.sequences[activeSequenceIndex];
  
  // Check if language is Hebrew for RTL support
  const isRTL = seance.language === 'Hebrew' || seance.language === 'he' || seance.language?.toLowerCase() === 'hebrew';

  // Functions to manage sequences and blocs
  const handleAddSequence = () => {
    setNewSequenceTitle('');
    setSequenceDialogOpen(true);
  };

  const handleCreateSequence = async () => {
    if (!newSequenceTitle.trim()) return;
    
    try {
      console.log('Creating new sequence with title:', newSequenceTitle);
      
      // Create a new sequence with just the title
      const newSequence = {
        subTheme: newSequenceTitle.trim(),
        blocs: [],

      };
      
      // Update the seance with the new sequence
      const updatedSeance = {
        ...seance,
        sequences: [...(seance.sequences || []), newSequence]
      };
      
      // Send PUT request to generation/seance/:seanceId (same as block saving)
      const response = await api.put(`generation/seance/${seanceId}`, updatedSeance);
      console.log('Save response:', response);
      
      // Fetch the updated seance from the server
      await updateSeance();
      
      // The seance state will be updated via the hook, so we can just close the dialog
      setSequenceDialogOpen(false);
      setNewSequenceTitle('');
      
      console.log('Sequence created successfully');
    } catch (error) {
      console.error('Error creating sequence:', error);
      alert('Erreur lors de la création de la séquence. Veuillez réessayer.');
    }
  };

  const handleCancelSequenceDialog = () => {
    setSequenceDialogOpen(false);
    setNewSequenceTitle('');
  };

  const handleEditSequenceTitle = (sequenceIndex) => {
    setEditingSequenceIndex(sequenceIndex);
    setEditingSequenceTitle(seance.sequences[sequenceIndex].subTheme);
  };

  const handleSaveSequenceTitle = async () => {
    if (!editingSequenceTitle.trim() || editingSequenceIndex === null) return;
    
    try {
    const updatedSequences = [...seance.sequences];
    updatedSequences[editingSequenceIndex] = {
      ...updatedSequences[editingSequenceIndex],
      subTheme: editingSequenceTitle.trim()
    };
    
    const updatedSeance = {
      ...seance,
      sequences: updatedSequences
    };
    
      // Send PUT request to update the seance
      await api.put(`generation/seance/${seanceId}`, updatedSeance);
      
      // Update local state
    setSeance(updatedSeance);
    setEditingSequenceIndex(null);
    setEditingSequenceTitle('');
      
      console.log('Sequence title updated successfully');
    } catch (error) {
      console.error('Error updating sequence title:', error);
      alert('Erreur lors de la mise à jour du titre de la séquence. Veuillez réessayer.');
    }
  };

  const handleCancelEditSequenceTitle = () => {
    setEditingSequenceIndex(null);
    setEditingSequenceTitle('');
  };

  const handleEditSeanceTitle = () => {
    setIsEditingTitle(true);
    setEditingTitle(seance.title || '');
  };

  const handleSaveSeanceTitle = async () => {
    if (!editingTitle.trim()) {
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);

    try {
      const updatedSeance = {
        ...seance,
        title: editingTitle.trim()
      };

      // Send PUT request to update the seance
      await api.put(`generation/seance/${seanceId}`, updatedSeance);

      // Update local state
      setSeance(updatedSeance);
      setIsEditingTitle(false);
      setEditingTitle('');

      console.log('Seance title updated successfully');
    } catch (error) {
      console.error('Error updating seance title:', error);
      alert('Erreur lors de la mise à jour du titre. Veuillez réessayer.');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleCancelEditSeanceTitle = () => {
    setIsEditingTitle(false);
    setEditingTitle('');
  };

  const handleRemoveSequence = (sequenceIndex) => {
    // Open confirmation dialog
    setSequenceToDelete(sequenceIndex);
    setDeleteSequenceDialogOpen(true);
  };

  const handleConfirmDeleteSequence = async () => {
    if (sequenceToDelete === null) return;
    
    if (seance.sequences && seance.sequences.length <= 1) {
      alert('Vous ne pouvez pas supprimer la dernière séquence.');
      setDeleteSequenceDialogOpen(false);
      setSequenceToDelete(null);
      return;
    }
    
    setIsDeletingSequence(true);
    try {
      const updatedSequences = seance.sequences.filter((_, index) => index !== sequenceToDelete);
      const updatedSeance = {
        ...seance,
        sequences: updatedSequences
      };
      
      // Send PUT request to update the seance
      await api.put(`generation/seance/${seanceId}`, updatedSeance);
      
      // Update local state
      setSeance(updatedSeance);
      
      // Adjust active sequence index if needed
      if (activeSequenceIndex >= updatedSequences.length) {
        setActiveSequenceIndex(updatedSequences.length - 1);
      }
      
      console.log('Sequence removed successfully');
      setDeleteSequenceDialogOpen(false);
      setSequenceToDelete(null);
    } catch (error) {
      console.error('Error removing sequence:', error);
      alert('Erreur lors de la suppression de la séquence. Veuillez réessayer.');
    } finally {
      setIsDeletingSequence(false);
    }
  };

  // Drag and drop handlers for sequences
  const handleDragStart = (e, sequenceIndex) => {
    if (isProcessingSequenceDrop) {
      e.preventDefault();
      return;
    }
    
    setDraggedSequenceIndex(sequenceIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sequenceIndex.toString());
    e.stopPropagation();
  };

  const handleDragEnd = (e) => {
    if (!isProcessingSequenceDrop) {
      setDraggedSequenceIndex(null);
      setDragOverIndex(null);
    }
  };

  const handleDragOver = (e, sequenceIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedSequenceIndex !== null && draggedSequenceIndex !== sequenceIndex && !isProcessingSequenceDrop) {
      setDragOverIndex(sequenceIndex);
    }
  };

  const handleDragLeave = (e) => {
    if (!isProcessingSequenceDrop) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isProcessingSequenceDrop || draggedSequenceIndex === null || draggedSequenceIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }
    
    setIsProcessingSequenceDrop(true);
    
    try {
      const sequences = [...seance.sequences];
      const draggedSequence = sequences[draggedSequenceIndex];
      
      // Remove the dragged sequence from its current position
      sequences.splice(draggedSequenceIndex, 1);
      
      // Calculate the correct insertion index after removal
      const insertIndex = draggedSequenceIndex < dropIndex ? dropIndex - 1 : dropIndex;
      
      // Insert it at the new position
      sequences.splice(insertIndex, 0, draggedSequence);
      
      const updatedSeance = {
        ...seance,
        sequences: sequences
      };
      
      // Send PUT request to update the seance
      await api.put(`generation/seance/${seanceId}`, updatedSeance);
      
      // Update local state
      setSeance(updatedSeance);
      
      // Update active sequence index if the moved sequence was active
      if (activeSequenceIndex === draggedSequenceIndex) {
        setActiveSequenceIndex(insertIndex);
      } else if (activeSequenceIndex > draggedSequenceIndex && activeSequenceIndex <= dropIndex) {
        setActiveSequenceIndex(activeSequenceIndex - 1);
      } else if (activeSequenceIndex < draggedSequenceIndex && activeSequenceIndex >= dropIndex) {
        setActiveSequenceIndex(activeSequenceIndex + 1);
      }
      
      console.log('Sequence moved successfully from position', draggedSequenceIndex, 'to', insertIndex);
    } catch (error) {
      console.error('Error moving sequence:', error);
      alert('Erreur lors du déplacement de la séquence. Veuillez réessayer.');
    } finally {
      setIsProcessingSequenceDrop(false);
      setDraggedSequenceIndex(null);
      setDragOverIndex(null);
    }
  };

  const handleAddBloc = async (blocType) => {
    if (!activeSequence) {
      console.warn('No active sequence to add bloc to');
      return;
    }
    
    // If it's a music block, open the music selection dialog
    if (blocType === 'music') {
      setMusicSelectionDialogOpen(true);
      setPendingMusicBlocInsertIndex(blocInsertIndex);
      setBlocMenuAnchor(null); // Close the menu
      return;
    }
    
    // If there's an insert index, add at that position
    if (blocInsertIndex !== null) {
      await handleAddBlocAtPosition(blocType, blocInsertIndex - 1);
      setBlocInsertIndex(null);
      setBlocMenuAnchor(null);
      return;
    }
    
    // If it's an image block, open the image generation dialog instead
    if (blocType === 'image') {
      setSequenceImageGenerationDialogOpen(true);
      setBlocMenuAnchor(null); // Close the menu
      return;
    }
    
    console.log('Adding bloc of type:', blocType, 'to active sequence:', activeSequenceIndex);
    
    setIsSavingBlock(true);
    try {
      // Get current blocs count to determine bloc_index
      const currentBlocs = activeSequence.blocs || [];
      const blocIndex = currentBlocs.length;
      
      const newBloc = {
        id: `bloc_${blocType}_${Date.now()}`,
        bloc_index: blocIndex,
        type_bloc: blocType,
        bloc_data: getDefaultBlocContent(blocType)
      };
      
      const updatedSequences = [...seance.sequences];
      updatedSequences[activeSequenceIndex] = {
        ...updatedSequences[activeSequenceIndex],
        blocs: [...(updatedSequences[activeSequenceIndex].blocs || []), newBloc]
      };
      
      const updatedSeance = {
        ...seance,
        sequences: updatedSequences
      };
      
      // Save to backend
      await api.put(`generation/seance/${seanceId}`, updatedSeance);
      
      // Refresh seance data from server
      await updateSeance();
      
      setBlocMenuAnchor(null); // Close the menu
      
      // Set visual feedback for newly added bloc
      setNewlyAddedBlocId(newBloc.id);
      
      // Clear the visual feedback after animation
      setTimeout(() => {
        setNewlyAddedBlocId(null);
      }, 2000);
    } catch (error) {
      console.error('Error adding bloc:', error);
      alert('Erreur lors de l\'ajout du bloc. Veuillez réessayer.');
    } finally {
      setIsSavingBlock(false);
    }
  };

  // Handle music selection and create block
  const handleSelectMusic = async (selectedMusic) => {
    if (!activeSequence) {
      console.warn('No active sequence to add bloc to');
      return;
    }
    
    setIsSavingBlock(true);
    try {
      const currentBlocs = activeSequence.blocs || [];
      let blocIndex;
      let insertPosition;
      
      // Determine insert position
      if (pendingMusicBlocInsertIndex !== null) {
        insertPosition = pendingMusicBlocInsertIndex;
        blocIndex = insertPosition;
      } else {
        blocIndex = currentBlocs.length;
        insertPosition = currentBlocs.length;
      }
      
      // Create new bloc with selected music data
      const newBloc = {
        id: `bloc_music_${Date.now()}`,
        bloc_index: blocIndex,
        type_bloc: 'music',
        bloc_data: {
          url: selectedMusic.url,
          name: selectedMusic.name
        }
      };
      
      const updatedBlocs = [...currentBlocs];
      
      // Insert at the specified position
      if (pendingMusicBlocInsertIndex !== null) {
        updatedBlocs.splice(insertPosition, 0, newBloc);
        // Update bloc_index for all blocs after insertion
        updatedBlocs.forEach((bloc, idx) => {
          bloc.bloc_index = idx;
        });
      } else {
        updatedBlocs.push(newBloc);
      }
      
      const updatedSequences = [...seance.sequences];
      updatedSequences[activeSequenceIndex] = {
        ...updatedSequences[activeSequenceIndex],
        blocs: updatedBlocs
      };
      
      const updatedSeance = {
        ...seance,
        sequences: updatedSequences
      };
      
      // Save to backend
      await api.put(`generation/seance/${seanceId}`, updatedSeance);
      
      // Refresh seance data from server
      await updateSeance();
      
      // Close dialog and reset state
      setMusicSelectionDialogOpen(false);
      setPendingMusicBlocInsertIndex(null);
      
      // Set visual feedback for newly added bloc
      setNewlyAddedBlocId(newBloc.id);
      
      // Clear the visual feedback after animation
      setTimeout(() => {
        setNewlyAddedBlocId(null);
      }, 2000);
    } catch (error) {
      console.error('Error adding music bloc:', error);
      alert('Erreur lors de l\'ajout du bloc musique. Veuillez réessayer.');
    } finally {
      setIsSavingBlock(false);
    }
  };

  const handleCloseMusicSelectionDialog = () => {
    setMusicSelectionDialogOpen(false);
    setPendingMusicBlocInsertIndex(null);
  };

  const handleAddBlocAtPosition = async (blocType, insertAfterIndex) => {
    if (!activeSequence) {
      console.warn('No active sequence to add bloc to');
      return;
    }
    
    // If it's a music block, open the music selection dialog
    if (blocType === 'music') {
      setMusicSelectionDialogOpen(true);
      setPendingMusicBlocInsertIndex(insertAfterIndex + 1);
      return;
    }
    
    // If it's an image block, open the image generation dialog instead
    if (blocType === 'image') {
      setSequenceImageGenerationDialogOpen(true);
      setBlocInsertIndex(insertAfterIndex + 1);
      return;
    }
    
    console.log('Adding bloc of type:', blocType, 'at position', insertAfterIndex + 1, 'in sequence:', activeSequenceIndex);
    
    setIsSavingBlock(true);
    try {
      const currentBlocs = activeSequence.blocs || [];
      const newBloc = {
        id: `bloc_${blocType}_${Date.now()}`,
        bloc_index: insertAfterIndex + 1,
        type_bloc: blocType,
        bloc_data: getDefaultBlocContent(blocType)
      };
      
      // Insert the new bloc after the specified index
      const updatedBlocs = [...currentBlocs];
      updatedBlocs.splice(insertAfterIndex + 1, 0, newBloc);
      
      // Update bloc_index for all blocs after the insertion point
      updatedBlocs.forEach((bloc, idx) => {
        bloc.bloc_index = idx;
      });
      
      const updatedSequences = [...seance.sequences];
      updatedSequences[activeSequenceIndex] = {
        ...updatedSequences[activeSequenceIndex],
        blocs: updatedBlocs
      };
      
      const updatedSeance = {
        ...seance,
        sequences: updatedSequences
      };
      
      // Save to backend
      await api.put(`generation/seance/${seanceId}`, updatedSeance);
      
      // Refresh seance data from server
      await updateSeance();
      
      // Set visual feedback for newly added bloc
      setNewlyAddedBlocId(newBloc.id);
      
      // Clear the visual feedback after animation
      setTimeout(() => {
        setNewlyAddedBlocId(null);
      }, 2000);
    } catch (error) {
      console.error('Error adding bloc at position:', error);
      alert('Erreur lors de l\'ajout du bloc. Veuillez réessayer.');
    } finally {
      setIsSavingBlock(false);
    }
  };

  const handleBlocMenuOpen = (event) => {
    setBlocMenuAnchor(event.currentTarget);
  };

  const handleBlocMenuClose = () => {
    setBlocMenuAnchor(null);
    setBlocInsertIndex(null);
  };

  const handleRemoveBloc = async (sequenceIndex, blocIndex) => {
    console.log('Removing bloc at index:', blocIndex, 'from sequence:', sequenceIndex);
    console.log('Original blocs count:', seance.sequences[sequenceIndex].blocs.length);
    console.log('Original blocs:', seance.sequences[sequenceIndex].blocs);
    
    setIsDeletingBloc(true);
    try {
      const updatedSequences = [...seance.sequences];
      const currentBlocs = [...updatedSequences[sequenceIndex].blocs];
      
      console.log('Before splice - blocs count:', currentBlocs.length);
      console.log('Before splice - blocs:', currentBlocs);
      
      // Remove the bloc at the specified index
      const removedBloc = currentBlocs.splice(blocIndex, 1);
      console.log('Removed bloc:', removedBloc);
      console.log('After splice - blocs count:', currentBlocs.length);
      console.log('After splice - blocs:', currentBlocs);
      
      // Update bloc_index for remaining blocs
      const updatedBlocs = currentBlocs.map((bloc, index) => ({
        ...bloc,
        bloc_index: index
      }));
      console.log('Updated blocs with new indices:', updatedBlocs);
      
      updatedSequences[sequenceIndex] = {
        ...updatedSequences[sequenceIndex],
        blocs: updatedBlocs
      };
      
      const updatedSeance = {
        ...seance,
        sequences: updatedSequences
      };
      
      console.log('Sending to API - sequences:', updatedSeance.sequences);
      console.log('Sending to API - target sequence blocs:', updatedSeance.sequences[sequenceIndex].blocs);
      
      // Send PUT request to update the seance
      const response = await api.put(`generation/seance/${seanceId}`, updatedSeance);
      console.log('Bloc removed successfully:', response);
      
      // Update local state with the response
      setSeance(updatedSeance);
      
      // Close the confirmation dialog
      setDeleteBlocDialogOpen(false);
      setBlocToDelete({ sequenceIndex: null, blocIndex: null });
      
    } catch (error) {
      console.error('Error removing bloc:', error);
      setRegenerationError('Erreur lors de la suppression du bloc');
    } finally {
      setIsDeletingBloc(false);
    }
  };

  const handleConfirmDeleteBloc = () => {
    if (blocToDelete.sequenceIndex !== null && blocToDelete.blocIndex !== null) {
      handleRemoveBloc(blocToDelete.sequenceIndex, blocToDelete.blocIndex);
    }
  };

  const handleMoveBloc = async (sequenceIndex, blocId, direction) => {
    console.log('Moving bloc:', blocId, direction, 'in sequence:', sequenceIndex);
    
    const sequence = seance.sequences[sequenceIndex];
    if (!sequence || !sequence.blocs) return;
    
    const blocs = [...sequence.blocs];
    const blocIndex = blocs.findIndex(block => block.id === blocId);
    
    if (blocIndex === -1) {
      console.warn('Block not found:', blocId);
      return;
    }
    
    const newIndex = direction === 'up' ? blocIndex - 1 : blocIndex + 1;
    
    // Check boundaries
    if (newIndex < 0 || newIndex >= blocs.length) {
      console.log('Cannot move block: out of bounds');
      return;
    }
    
    // Set moving state for visual feedback
    setMovingBlockId(blocId);
    
    try {
      // Small delay for visual feedback
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Swap blocs
      [blocs[blocIndex], blocs[newIndex]] = [blocs[newIndex], blocs[blocIndex]];
      
      // Update bloc_index for all blocs to reflect their new positions
      const updatedBlocs = blocs.map((bloc, index) => ({
        ...bloc,
        bloc_index: index
      }));
      
      const updatedSequences = [...seance.sequences];
      updatedSequences[sequenceIndex] = {
        ...updatedSequences[sequenceIndex],
        blocs: updatedBlocs
      };
      
      const updatedSeance = {
        ...seance,
        sequences: updatedSequences
      };
      
      console.log('Sending to API - sequences:', updatedSeance.sequences);
      console.log('Sending to API - target sequence blocs:', updatedSeance.sequences[sequenceIndex].blocs);
      
      // Send PUT request to update the seance
      const response = await api.put(`generation/seance/${seanceId}`, updatedSeance);
      console.log('Bloc moved successfully:', response);
      
      // Update local state with the response
      setSeance(updatedSeance);
      console.log('Block moved successfully from position', blocIndex, 'to', newIndex);
    } catch (error) {
      console.error('Error moving bloc:', error);
      setRegenerationError('Erreur lors du déplacement du bloc');
    } finally {
      setMovingBlockId(null); // Clear moving state
    }
  };


  // Handle sequence editing
  const handleEditSequence = () => {
    if (!seance || !seance.sequences[activeSequenceIndex]) return;
    
    // Load current sequence prompt if it exists
    const currentSequence = seance.sequences[activeSequenceIndex];
    if (currentSequence?.config?.prompt) {
      setEditSequencePrompt(currentSequence.config.prompt);
    } else {
      setEditSequencePrompt('');
    }
    
    setEditSequenceDialogOpen(true);
  };

  const handleSubmitEditCurrentSequence = async () => {
    if (!editSequencePrompt.trim()) return;
    
    console.log('Editing current sequence with prompt:', editSequencePrompt);
    
    try {
      // TODO: Replace with actual API call
      // const result = await api.post(`/seances/${seanceId}/sequences/${activeSequenceIndex}/edit`, { prompt: editSequencePrompt });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update current sequence with new prompt
      const updatedSequences = [...seance.sequences];
      const currentSequence = updatedSequences[activeSequenceIndex];
      
      if (currentSequence) {
        updatedSequences[activeSequenceIndex] = {
          ...currentSequence,
          config: {
            ...currentSequence.config,
            prompt: editSequencePrompt.trim()
          },
          updated_at: new Date().toISOString()
        };
        
        const updatedSeance = {
          ...seance,
          sequences: updatedSequences,
          updated_at: new Date().toISOString()
        };
        
        setSeance(updatedSeance);
        setEditSequenceDialogOpen(false);
        
        console.log('Sequence edited successfully');
      }
    } catch (error) {
      console.error('Error editing sequence:', error);
    }
  };

  const handleCancelEditCurrentSequence = () => {
    setEditSequenceDialogOpen(false);
    setEditSequencePrompt('');
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setRegenerationError(null);
    setSeancePrompt('');
    setRetryCount(0);
    
    // Clear any pending timeout
    if (regenerationTimeoutId) {
      clearTimeout(regenerationTimeoutId);
      setRegenerationTimeoutId(null);
    }
  };

  const handleRetryRegeneration = () => {
    setRetryCount(prev => prev + 1);
    setRegenerationError(null);
    handleRegenerateSeance();
  };

  // Handle seance deletion
  const handleDeleteSeance = async () => {
    setIsDeleting(true);
    
    try {
      console.log('Deleting seance:', seanceId);
      
      // Call the API to delete the seance
      await api.seance.deleteSeance(seanceId);
      
      console.log('Seance deleted successfully');
      
      // Navigate back to seances list
      navigate('/seance');
    } catch (error) {
      console.error('Error deleting seance:', error);
      alert('Erreur lors de la suppression de la séance. Veuillez réessayer.');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Handle seance regeneration with prompt
  const handleRegenerateSeance = async () => {
    if (!seancePrompt.trim()) return;
    
    setIsRegeneratingSeance(true);
    setRegenerationError(null);
    
    // Set a client-side timeout as additional safety net (3 minutes)
    const timeoutId = setTimeout(() => {
      if (isRegeneratingSeance) {
        setIsRegeneratingSeance(false);
        setRegenerationError('La régénération prend plus de temps que prévu. Veuillez réessayer.');
        console.warn('Client-side timeout triggered for regeneration');
      }
    }, 180000); // 3 minutes
    
    setRegenerationTimeoutId(timeoutId);
    
    try {
      console.log('Regenerating seance with prompt:', seancePrompt);
      
      // Call the API to regenerate the seance
      await api.seance.updateSeanceWithPrompt(seanceId, seancePrompt);
      
      // Clear the timeout since request completed
      clearTimeout(timeoutId);
      setRegenerationTimeoutId(null);
      
      // Refresh seance data to get the updated content
      await updateSeance();
      
      // Close dialog and reset prompt immediately
      setChatDialogOpen(false);
      setSeancePrompt('');
      
      console.log('Seance regenerated successfully');
    } catch (error) {
      console.error('Error regenerating seance:', error);
      
      // Clear the timeout since request failed
      clearTimeout(timeoutId);
      setRegenerationTimeoutId(null);
      
      // Set user-friendly error message
      let errorMessage = 'Une erreur est survenue lors de la régénération.';
      
      if (error.message.includes('timeout')) {
        errorMessage = 'La régénération prend plus de temps que prévu. Veuillez réessayer.';
      } else if (error.message.includes('User not authenticated')) {
        errorMessage = 'Votre session a expiré. Veuillez vous reconnecter.';
      } else if (error.message.includes('API error')) {
        errorMessage = 'Erreur du serveur. Veuillez réessayer plus tard.';
      }
      
      setRegenerationError(errorMessage);
    } finally {
      setIsRegeneratingSeance(false);
    }
  };

  // Handle saving block changes
  const handleSaveBlock = async (updatedBlock) => {
    if (!seance || !activeSequence) return;
    
    setIsSavingBlock(true);
    try {
      // Create updated seance data
      const updatedSeance = {
        ...seance,
        sequences: seance.sequences.map((sequence, seqIndex) => 
          seqIndex === activeSequenceIndex 
            ? {
                ...sequence,
                blocs: sequence.blocs.map(block => 
                  block.id === updatedBlock.id ? {
                    ...block,
                    bloc_data: updatedBlock.bloc_data
                  } : block
                )
              }
            : sequence
        )
      };
      
      // Send PUT request to generation/seance/:seanceId
      const response = await api.put(`generation/seance/${seanceId}`, updatedSeance);
      console.log('Save response:', response);
      
      // Refresh seance data from server to get latest updates (especially after image regeneration)
      await updateSeance();
      
      console.log('Block saved successfully');
    } catch (error) {
      console.error('Error saving block:', error);
    } finally {
      setIsSavingBlock(false);
    }
  };

  // Handle validating a block
  const handleValidateBlock = async (blockId) => {
    if (!seance || !activeSequence) return;
    
    setIsSavingBlock(true);
    try {
      // Create updated seance data with status set to "text_validated"
      const updatedSeance = {
        ...seance,
        sequences: seance.sequences.map((sequence, seqIndex) => 
          seqIndex === activeSequenceIndex 
            ? {
                ...sequence,
                blocs: sequence.blocs.map(block => 
                  block.id === blockId ? {
                    ...block,
                    bloc_data: {
                      ...block.bloc_data,
                      status: 'text_validated'
                    }
                  } : block
                )
              }
            : sequence
        )
      };
      
      // Send PUT request to generation/seance/:seanceId
      const response = await api.put(`generation/seance/${seanceId}`, updatedSeance);
      console.log('Validation response:', response);
      
      // Update local state with the response
      setSeance(updatedSeance);
      
      console.log('Block validated successfully');
    } catch (error) {
      console.error('Error validating block:', error);
      alert('Erreur lors de la validation du bloc. Veuillez réessayer.');
    } finally {
      setIsSavingBlock(false);
    }
  };

  // AI bloc modification handlers
  // Block actions menu handlers
  const handleOpenBlockActionsMenu = (event, blockIndex) => {
    setBlockActionsMenuAnchor(event.currentTarget);
    setBlockActionsMenuBlockIndex(blockIndex);
  };

  const handleCloseBlockActionsMenu = () => {
    setBlockActionsMenuAnchor(null);
    setBlockActionsMenuBlockIndex(null);
  };

  // Sequence actions menu handlers
  const handleOpenSequenceActionsMenu = (event, sequenceIndex) => {
    event.stopPropagation();
    setSequenceActionsMenuAnchor(event.currentTarget);
    setSequenceActionsMenuIndex(sequenceIndex);
  };

  const handleCloseSequenceActionsMenu = () => {
    setSequenceActionsMenuAnchor(null);
    setSequenceActionsMenuIndex(null);
  };

  const handleOpenAiBlocDialog = (blocIndex) => {
    handleCloseBlockActionsMenu();
    setAiBlocIndex(blocIndex);
    setAiBlocPrompt('');
    setAiBlocDialogOpen(true);
  };

  const handleOpenImageGenerationFromMenu = (blockId) => {
    handleCloseBlockActionsMenu();
    handleOpenImageGenerationDialog(blockId);
  };

  const handleOpenImageBank = async (blockIndex) => {
    handleCloseBlockActionsMenu();
    setImageBankBlockIndex(blockIndex);
    setSelectedGalleryImages(new Set());
    setImageBankDialogOpen(true);
    
    // Fetch gallery images
    setLoadingGalleryImages(true);
    try {
      const response = await galleryImageApi.getAllImages();
      if (response.success && response.data && response.data.images) {
        setGalleryImages(response.data.images);
      } else {
        setGalleryImages([]);
      }
    } catch (err) {
      console.error('Error fetching gallery images:', err);
      setGalleryImages([]);
    } finally {
      setLoadingGalleryImages(false);
    }
  };

  const handleCloseImageBank = () => {
    setImageBankDialogOpen(false);
    setImageBankBlockIndex(null);
    setSelectedGalleryImages(new Set());
  };

  const handleToggleGalleryImageSelection = (imageId) => {
    setSelectedGalleryImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const handleImportGalleryImages = async () => {
    if (selectedGalleryImages.size === 0 || imageBankBlockIndex === null) return;
    
    setIsSavingBlock(true);
    try {
      const blockIndex = imageBankBlockIndex;
      const sequenceIndex = activeSequenceIndex;
      
      // Get selected images from gallery
      const imagesToImport = galleryImages.filter(img => selectedGalleryImages.has(img.id));
      
      // Upload each image one by one using the same API as file upload but with URL
      for (const image of imagesToImport) {
        try {
          await api.seance.uploadImageBlockFromUrl(
            seanceId,
            sequenceIndex,
            blockIndex,
            image.url,
            image.name || null
          );
        } catch (error) {
          console.error(`Error uploading image ${image.id}:`, error);
          // Continue with other images even if one fails
        }
      }
      
      // Refresh seance data
      await updateSeance();
      
      handleCloseImageBank();
      alert(`${imagesToImport.length} image(s) importée(s) avec succès!`);
    } catch (error) {
      console.error('Error importing gallery images:', error);
      alert('Erreur lors de l\'importation des images. Veuillez réessayer.');
    } finally {
      setIsSavingBlock(false);
    }
  };

  const handleUploadImageClick = (blockIndex) => {
    handleCloseBlockActionsMenu();
    setUploadingBlockIndex(blockIndex);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || uploadingBlockIndex === null) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const blockIndex = uploadingBlockIndex;
    const sequenceIndex = activeSequenceIndex;

    setIsUploadingImage(true);

    try {
      // Use the API function which handles base64 conversion
      const result = await api.seance.uploadImageBlock(seanceId, sequenceIndex, blockIndex, file);
      
      console.log('Image uploaded successfully:', result);

      // Refresh the seance data
      await updateSeance();

      alert('Image uploadée avec succès!');
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadErrorMessage(error.message || 'Une erreur est survenue lors de l\'upload de l\'image.');
      setUploadErrorDialogOpen(true);
    } finally {
      setIsUploadingImage(false);
      setUploadingBlockIndex(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCloseAiBlocDialog = () => {
    setAiBlocDialogOpen(false);
    setAiBlocPrompt('');
    setAiBlocIndex(null);
  };

  const handleModifyBloc = async () => {
    if (!aiBlocPrompt.trim() || aiBlocIndex === null) return;
    
    setIsModifyingBloc(true);
    try {
      const response = await api.seance.modifyBloc(seanceId, aiBlocIndex, activeSequenceIndex, aiBlocPrompt);
      console.log('Bloc modified:', response);
      
      // Refresh the seance data to get the updated bloc
      await updateSeance();
      
      handleCloseAiBlocDialog();
    } catch (error) {
      console.error('Error modifying bloc:', error);
      setRegenerationError('Erreur lors de la modification du bloc');
    } finally {
      setIsModifyingBloc(false);
    }
  };

  // Image generation handlers
  const handleOpenImageGenerationDialog = (blockId) => {
    setImageGenerationBlockId(blockId);
    setImageGenerationPrompt('');
    setImageGenerationNbVariations(2);
    setImageGenerationStyle('cartoon');
    setImageGenerationAspectRatio('16:9');
    setImageGenerationDialogOpen(true);
  };

  const handleCloseImageGenerationDialog = () => {
    setImageGenerationDialogOpen(false);
    setImageGenerationBlockId(null);
    setImageGenerationPrompt('');
    setImageGenerationNbVariations(2);
    setImageGenerationStyle('cartoon');
    setImageGenerationAspectRatio('16:9');
  };

  const handleGenerateImages = async () => {
    if (!imageGenerationPrompt.trim() || !imageGenerationBlockId || !selectedImageGenerationModel) return;
    
    setIsGeneratingImages(true);
    try {
      // Find the block to get its index
      const block = activeSequence.blocs.find(b => b.id === imageGenerationBlockId);
      if (!block) {
        throw new Error('Block not found');
      }
      
      // Store the current number of images before generation (this will be the index of the first new image)
      const oldImagesLength = (block.bloc_data?.images || block.content?.images || []).length;
      
      const blocIndex = activeSequence.blocs.findIndex(b => b.id === imageGenerationBlockId);
      
      // Call API to generate images for the block
      const response = await api.seance.generateBlockImages(
        seanceId,
        blocIndex,
        activeSequenceIndex,
        imageGenerationPrompt,
        imageGenerationNbVariations,
        imageGenerationStyle,
        selectedImageGenerationModel || 'google/gemini-2.5-flash-image',
        imageGenerationAspectRatio
      );
      
      console.log('Images generated:', response);
      
      // Refresh the seance data to get the generated images
      await updateSeance();
      
      handleCloseImageGenerationDialog();
      
      // Trigger opening regenerate dialog for the first newly generated image
      // The first new image will be at index = oldImagesLength (0-indexed)
      // Wait a bit for the seance to update and images to be available
      setTimeout(() => {
        setOpenRegenerateDialogForBlock({
          blockId: imageGenerationBlockId,
          imageIndex: oldImagesLength // First newly generated image
        });
      }, 800); // Increased timeout to ensure images are loaded
    } catch (error) {
      console.error('Error generating images:', error);
      alert('Erreur lors de la génération des images. Veuillez réessayer.');
    } finally {
      setIsGeneratingImages(false);
    }
  };

  const handleGenerateImagesForSequence = async () => {
    if (!sequenceImageGenerationPrompt.trim() || !selectedSequenceImageGenerationModel) return;
    
    setIsGeneratingSequenceImages(true);
    try {
      // Call API to generate images for the sequence
      const response = await api.seance.generateImagesForSequence(
        seanceId,
        activeSequenceIndex,
        sequenceImageGenerationPrompt,
        sequenceImageGenerationNbVariations,
        sequenceImageGenerationStyle,
        selectedSequenceImageGenerationModel || 'google/gemini-2.5-flash-image',
        sequenceImageGenerationAspectRatio
      );
      
      console.log('Images generated for sequence:', response);
      
      // Refresh the seance data to get the generated images and the new image block
      await updateSeance();
      
      // Close the dialog and reset form
      setSequenceImageGenerationDialogOpen(false);
      setSequenceImageGenerationPrompt('');
      setSequenceImageGenerationNbVariations(2);
      setSequenceImageGenerationStyle('cartoon');
      setSequenceImageGenerationAspectRatio('16:9');
    } catch (error) {
      console.error('Error generating images for sequence:', error);
      alert('Erreur lors de la génération des images. Veuillez réessayer.');
    } finally {
      setIsGeneratingSequenceImages(false);
    }
  };

  // Handle Word document export


  // Helper function to get default content for different bloc types
  const getDefaultBlocContent = (blocType) => {
    switch (blocType) {
      case 'image':
        return {
          images: [
            {
              image_index: 0,
              img_url: 'https://via.placeholder.com/300x200/3498DB/FFFFFF?text=Nouvelle+Image'
            }
          ]
        };
      case 'teacher_notes':
        return {
          notes: [
            {
              title: 'Simple Explanation',
              content: 'Nouveau contenu pour les notes du professeur...'
            }
          ],
          images: []
        };
      case 'quiz':
        return {
          question: 'Nouvelle question?',
          answers: [
            {
              answer_index: 0,
              answer: 'Réponse 1',
              outcome: 'good answer'
            },
            {
              answer_index: 1,
              answer: 'Réponse 2',
              outcome: 'bad answer'
            },
            {
              answer_index: 2,
              answer: 'Réponse 3',
              outcome: 'bad answer'
            }
          ],
          images: []
        };
      case 'music':
        // This will be set when user selects a music
        return {
          url: '',
          name: 'Nouvelle musique'
        };
      case 'sondage':
        return {
          question: 'Nouvelle question?',
          answers: [
            {
              answer_index: 0,
              answer: 'Réponse 1'
            },
            {
              answer_index: 1,
              answer: 'Réponse 2'
            },
            {
              answer_index: 2,
              answer: 'Réponse 3'
            }
          ],
          images: []
        };
      default:
        return {};
    }
  };

  const handleDuplicateSeance = async () => {
    if (!seanceId) {
      alert('Aucune séance à dupliquer');
      return;
    }

    setIsDuplicating(true);

    try {
      const response = await api.seance.duplicateSeance(seanceId);
      
      if (response.success && response.data?.duplicate_seance_id) {
        // Navigate to the duplicated seance
        navigate(`/seance/${response.data.duplicate_seance_id}`);
      } else {
        throw new Error(response.message || 'Erreur lors de la duplication');
      }
    } catch (error) {
      console.error('Error duplicating seance:', error);
      alert(`Erreur lors de la duplication: ${error.message}`);
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleOpenTranslateMenu = (event) => {
    setTranslateMenuAnchor(event.currentTarget);
  };

  const handleCloseTranslateMenu = () => {
    setTranslateMenuAnchor(null);
  };

  const handleOpenInfoMenu = (event) => {
    setInfoMenuAnchor(event.currentTarget);
  };

  const handleCloseInfoMenu = () => {
    setInfoMenuAnchor(null);
    setExportMenuAnchor(null);
  };

  const handleOpenExportMenu = (event) => {
    event.stopPropagation();
    setExportMenuAnchor(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchor(null);
  };

  const handleTranslate = async (language) => {
    if (!seanceId) {
      alert('Aucune séance à traduire');
      return;
    }

    setIsTranslating(true);
    setTranslatingLanguage(language);

    try {
      const response = await api.seance.translateSeance(seanceId, language);
      
      if (response.success) {
        // Refresh the seance data from the hook
        await updateSeance();
        
        // Also fetch and update the local seance state directly
        const fetchedResponse = await api.seance.getSeanceById(seanceId);
        const seanceData = fetchedResponse.data?.seance || fetchedResponse;
        const seanceWithIds = {
          ...seanceData,
          sequences: (seanceData.sequences || []).map(sequence => ({
            ...sequence,
            blocs: (sequence.blocs || []).map(bloc => ({
              ...bloc,
              id: bloc.id || `bloc_${bloc.bloc_index ?? Date.now()}`
            }))
          }))
        };
        setSeance(seanceWithIds);
      } else {
        throw new Error(response.message || 'Erreur lors de la traduction');
      }
    } catch (error) {
      console.error('Error translating seance:', error);
      alert(`Erreur lors de la traduction: ${error.message}`);
    } finally {
      setIsTranslating(false);
      setTranslatingLanguage(null);
      // Close the menu after translation completes
      handleCloseTranslateMenu();
    }
  };

  // Collect all images from all blocks in all sequences
  const collectAllSeanceImages = () => {
    if (!seance || !seance.sequences) {
      console.log('No seance or sequences found', { seance, hasSequences: seance?.sequences });
      return [];
    }
    
    const images = [];
    console.log('Starting image collection, sequences count:', seance.sequences.length);
    
    seance.sequences.forEach((sequence, sequenceIndex) => {
      // Handle both 'blocs' and 'blocks' property names
      const blocks = sequence.blocs || sequence.blocks || [];
      if (blocks.length > 0) {
        console.log(`Sequence ${sequenceIndex}: ${blocks.length} blocks`);
        blocks.forEach((block, blockIndex) => {
          // Prioritize bloc_data.images as user confirmed images are there
          const blockImages = block.bloc_data?.images || block.content?.images || [];
          console.log(`Block ${blockIndex} (type: ${block.type_bloc || block.bloc_type}): ${blockImages.length} images`, {
            hasBlocData: !!block.bloc_data,
            hasContent: !!block.content,
            blocDataImages: block.bloc_data?.images?.length,
            contentImages: block.content?.images?.length,
            firstImage: blockImages[0]
          });
          
          blockImages.forEach((image, imageIndex) => {
            // Handle both string URLs and image objects
            let imageUrl = null;
            if (typeof image === 'string') {
              imageUrl = image;
              console.log(`Image ${imageIndex} is string:`, imageUrl);
            } else if (image && typeof image === 'object') {
              // Prioritize img_url as user confirmed that's the property name
              imageUrl = image.img_url || image.original_url || image.url || image.compressed_url || image.image_url;
              if (!imageUrl) {
                console.warn(`Image ${imageIndex} has no URL property:`, Object.keys(image));
              }
            }
            
            if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
              // Use subTheme if title is not available (as per the actual data structure)
              const sequenceTitle = sequence.title || sequence.subTheme || `Séquence ${sequenceIndex + 1}`;
              images.push({
                url: imageUrl,
                imageObject: typeof image === 'object' ? image : { img_url: imageUrl },
                sequenceIndex,
                blockIndex,
                imageIndex,
                sequenceTitle: sequenceTitle,
                blockType: block.type_bloc || block.bloc_type || 'image'
              });
            } else {
              console.warn('Skipping invalid image:', { 
                image, 
                blockIndex, 
                imageIndex,
                imageType: typeof image,
                isObject: image && typeof image === 'object',
                hasImgUrl: image?.img_url,
                imageKeys: image && typeof image === 'object' ? Object.keys(image) : null
              });
            }
          });
        });
      } else {
        console.log(`Sequence ${sequenceIndex} has no blocs/blocks`);
      }
    });
    
    console.log('Collected images total:', images.length);
    if (images.length > 0) {
      console.log('First few collected images:', images.slice(0, 3).map(img => ({ 
        url: img.url.substring(0, 50) + '...', 
        sequence: img.sequenceTitle,
        block: img.blockIndex,
        image: img.imageIndex
      })));
    }
    return images;
  };

  // Handle opening thumbnail selection dialog
  const handleOpenThumbnailDialog = async () => {
    // Ensure we have the latest seance data
    if (updateSeance) {
      await updateSeance();
    }
    
    // Small delay to ensure state is updated
    setTimeout(() => {
      const images = collectAllSeanceImages();
      console.log('Opening thumbnail dialog with images:', images);
      console.log('Current seance state:', seance);
      
      if (images.length === 0) {
        alert('Aucune image trouvée dans cette séance. Veuillez d\'abord ajouter des images aux blocs.\n\nVérifiez la console pour plus de détails.');
        return;
      }
      setAllSeanceImages(images);
      setThumbnailDialogOpen(true);
    }, 100);
  };

  // Handle closing thumbnail selection dialog
  const handleCloseThumbnailDialog = () => {
    setThumbnailDialogOpen(false);
    setAllSeanceImages([]);
  };

  // Handle setting thumbnail
  const handleSetThumbnail = async (imageObject) => {
    setIsSettingThumbnail(true);
    try {
      const response = await api.seance.setThumbnail(seanceId, imageObject);
      if (response.success || response) {
        // Refresh the seance data
        await updateSeance();
        // Close the dialog without showing alert
        handleCloseThumbnailDialog();
      } else {
        throw new Error(response.message || 'Erreur lors de la définition de la miniature');
      }
    } catch (error) {
      console.error('Error setting thumbnail:', error);
      alert(`Erreur lors de la définition de la miniature: ${error.message}`);
    } finally {
      setIsSettingThumbnail(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1.02); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1.02); }
          }
        `}
      </style>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minHeight: '100vh',
          alignItems: 'flex-start',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: aiChatDialogOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
            transition: 'width 0.3s ease-in-out'
          }}
        >
          <Container 
            maxWidth="xl"
            sx={{ 
              py: 3,
              width: '100%',
              margin: '0 auto',
              overflow: 'visible'
            }}
          >
        {/* Global Seance Information - Separate Box */}
        <Card sx={{ 
          borderRadius: 4, 
          bgcolor: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          mb: 2
        }}>
          <CardContent sx={{ p: 1.5 }}>
            {/* All Global Information on One Row */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Back Button */}
                <Button
                  onClick={() => navigate('/seance')}
                  startIcon={<ArrowBack />}
                  variant="outlined"
                size="small"
                  sx={{ 
                    borderColor: '#FF9800',
                    color: '#E65100',
                    fontWeight: 'bold',
                    '&:hover': {
                      borderColor: '#F57C00',
                      backgroundColor: '#FFF3E0'
                    }
                  }}
                >
                Retour
                </Button>
                
              {/* Title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                    {isEditingTitle ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                    <TextField
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      variant="outlined"
                      size="small"
                      disabled={isSavingTitle}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          color: '#E65100',
                          '& fieldset': {
                            borderColor: '#E65100',
                            borderWidth: 2
                          },
                          '&:hover fieldset': {
                            borderColor: '#F57C00'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#E65100'
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveSeanceTitle();
                        } else if (e.key === 'Escape') {
                          handleCancelEditSeanceTitle();
                        }
                      }}
                      autoFocus
                    />
                    <IconButton
                      onClick={handleSaveSeanceTitle}
                      disabled={isSavingTitle}
                      size="small"
                      sx={{
                        color: '#10b981',
                        '&:hover': {
                          backgroundColor: '#f0fdf4'
                        }
                      }}
                    >
                      {isSavingTitle ? <CircularProgress size={18} /> : <SaveIcon />}
                    </IconButton>
                    <IconButton
                      onClick={handleCancelEditSeanceTitle}
                      disabled={isSavingTitle}
                      size="small"
                      sx={{
                        color: '#E53935',
                        '&:hover': {
                          backgroundColor: '#FFEBEE'
                        }
                      }}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      onClick={handleEditSeanceTitle}
                      sx={{
                        fontWeight: 'bold', 
                        color: '#E65100',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          opacity: 0.8
                        }
                      }}
                    >
                      {seance.title}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Metadata - Compact */}
              <Typography variant="body2" sx={{ color: '#7F8C8D', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                {seance.theme} | {seance.age} | {getLevelLabel(seance.level)} | {seance.language}
              </Typography>
                
              {/* Thumbnail Display - Compact */}
                {seance?.thumbnail && (() => {
                  // Extract thumbnail URL - handle both object and string formats
                  const thumbnailUrl = typeof seance.thumbnail === 'string' 
                    ? seance.thumbnail 
                    : (seance.thumbnail.img_url || seance.thumbnail.original_url || seance.thumbnail.url || seance.thumbnail.compressed_url);
                  
                  if (!thumbnailUrl) return null;
                  
                  return (
                      <Box
                        sx={{
                          position: 'relative',
                      borderRadius: '6px',
                          overflow: 'hidden',
                      border: '2px solid #ec4899',
                      boxShadow: '0 2px 8px rgba(236, 72, 153, 0.2)',
                      width: '100px',
                      height: '60px',
                      flexShrink: 0
                        }}
                      >
                        <Box
                          component="img"
                          src={thumbnailUrl}
                          alt="Miniature de la séance"
                          sx={{
                            width: '100%',
                        height: '100%',
                            display: 'block',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300x200?text=Thumbnail+Error';
                          }}
                        />
                    {/* Small button to change thumbnail */}
                    <IconButton
                          sx={{
                            position: 'absolute',
                        bottom: 2,
                        right: 2,
                        bgcolor: 'rgba(236, 72, 153, 0.95)',
                            color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(236, 72, 153, 1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        width: 28,
                        height: 28,
                        p: 0.5
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenThumbnailDialog();
                      }}
                      disabled={isSettingThumbnail || !seance}
                      size="small"
                      title="Changer la miniature"
                    >
                      {isSettingThumbnail ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <PhotoCameraIcon sx={{ fontSize: '0.875rem' }} />
                      )}
                    </IconButton>
                        </Box>
                  );
                })()}
              
              {/* Présenter Button */}
              <Button
                onClick={() => navigate(`/seance/presentation/${seanceId}`)}
                startIcon={<SlideshowIcon />}
                variant="contained"
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 2.5,
                  py: 1,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Présenter
              </Button>
              
              {/* Info Menu Button */}
              <IconButton
                ref={infoMenuButtonRef}
                onClick={handleOpenInfoMenu}
                size="small"
                sx={{ 
                  color: '#6b7280',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                    color: '#374151'
                  }
                }}
              >
                <MoreVertIcon />
              </IconButton>
              
              {/* Info Menu */}
              <Menu
                anchorEl={infoMenuAnchor}
                open={Boolean(infoMenuAnchor)}
                onClose={handleCloseInfoMenu}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <ListItemIcon>
                    <ScheduleIcon sx={{ fontSize: '1.2rem', color: '#FF9800' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`Créé le ${new Date(seance.created_at).toLocaleDateString('fr-FR')}`}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </MenuItem>
                <MenuItem disabled>
                  <ListItemIcon>
                    <ScheduleIcon sx={{ fontSize: '1.2rem', color: '#FF9800' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`Modifié le ${new Date(seance.updated_at).toLocaleDateString('fr-FR')}`}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </MenuItem>
                
                <MenuItem divider />
                
                {/* Duplicate */}
                <MenuItem 
                  onClick={() => {
                    handleCloseInfoMenu();
                    handleDuplicateSeance();
                  }}
                  disabled={isDuplicating}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {isDuplicating ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: '1.2rem', color: '#10b981' }} />
                  )}
                  <ListItemText primary={isDuplicating ? 'Duplication...' : 'Dupliquer'} />
                </MenuItem>
                
                {/* Translate */}
                <MenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseInfoMenu();
                    // Use the info button as anchor for translate menu
                    if (infoMenuButtonRef.current) {
                      setTranslateMenuAnchor(infoMenuButtonRef.current);
                    }
                  }}
                  disabled={isTranslating}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {isTranslating ? (
                    <CircularProgress size={16} />
                  ) : (
                    <TranslateIcon sx={{ fontSize: '1.2rem', color: '#8b5cf6' }} />
                  )}
                  <ListItemText primary={isTranslating ? 'Traduction...' : 'Traduire'} />
                </MenuItem>
                
                {/* Thumbnail */}
                <MenuItem 
                  onClick={() => {
                    handleCloseInfoMenu();
                    handleOpenThumbnailDialog();
                  }}
                  disabled={isSettingThumbnail || !seance}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {isSettingThumbnail ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PhotoCameraIcon sx={{ fontSize: '1.2rem', color: '#ec4899' }} />
                  )}
                  <ListItemText primary={isSettingThumbnail ? 'Définition...' : 'Miniature'} />
                </MenuItem>
                
                {/* Exports */}
                <MenuItem 
                  onClick={handleOpenExportMenu}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CsvIcon sx={{ fontSize: '1.2rem', color: '#2563eb' }} />
                  <ListItemText primary="Exports" />
                </MenuItem>
                
                {/* Delete */}
                <MenuItem 
                  onClick={() => {
                    handleCloseInfoMenu();
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <DeleteIcon sx={{ fontSize: '1.2rem', color: '#E53935' }} />
                  <ListItemText primary="Suppression" />
                </MenuItem>
              </Menu>
              
              {/* Export Submenu */}
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleCloseExportMenu}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <ExportButtons 
                  seanceId={seanceId} 
                  seance={seance} 
                  asMenuItems={true}
                  onClose={handleCloseExportMenu}
                />
              </Menu>
              
              {/* Translate Submenu */}
              <Menu
                anchorEl={translateMenuAnchor}
                open={Boolean(translateMenuAnchor)}
                onClose={isTranslating ? undefined : handleCloseTranslateMenu}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <MenuItem 
                  onClick={() => handleTranslate('French')}
                  disabled={isTranslating}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {translatingLanguage === 'French' && isTranslating ? (
                    <CircularProgress size={16} />
                  ) : null}
                  <ListItemText primary={translatingLanguage === 'French' && isTranslating ? 'Traduction en cours...' : 'Français'} />
                </MenuItem>
                <MenuItem 
                  onClick={() => handleTranslate('Italian')}
                  disabled={isTranslating}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {translatingLanguage === 'Italian' && isTranslating ? (
                    <CircularProgress size={16} />
                  ) : null}
                  <ListItemText primary={translatingLanguage === 'Italian' && isTranslating ? 'Traduction en cours...' : 'Italien'} />
                </MenuItem>
                <MenuItem 
                  onClick={() => handleTranslate('Hebrew')}
                  disabled={isTranslating}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {translatingLanguage === 'Hebrew' && isTranslating ? (
                    <CircularProgress size={16} />
                  ) : null}
                  <ListItemText primary={translatingLanguage === 'Hebrew' && isTranslating ? 'Traduction en cours...' : 'Hébreu'} />
                </MenuItem>
                <MenuItem 
                  onClick={() => handleTranslate('English')}
                  disabled={isTranslating}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {translatingLanguage === 'English' && isTranslating ? (
                    <CircularProgress size={16} />
                  ) : null}
                  <ListItemText primary={translatingLanguage === 'English' && isTranslating ? 'Traduction en cours...' : 'Anglais'} />
                </MenuItem>
              </Menu>
            </Box>
          </CardContent>
        </Card>

            {/* Generation Status - Only show for actual generation in progress */}
            {seance.generation_status?.status === 'in_progress' && (
              <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#FFF3E0', border: '1px solid #FFCC02' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#F57C00' }} />
                    <Typography variant="h6" sx={{ color: '#E65100', fontWeight: 'bold' }}>
                      Génération en cours...
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#F57C00', ml: 'auto' }}>
                      {seance.generation_status.progress_percentage}% terminé
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ 
                      width: '100%', 
                      height: 8, 
                      backgroundColor: '#FFE0B2', 
                      borderRadius: 4,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        width: `${seance.generation_status.progress_percentage}%`,
                        height: '100%',
                        backgroundColor: '#F57C00',
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

        {/* Sequences and Blocks Content - Separate Box */}
        <Card sx={{ 
          borderRadius: 4, 
          bgcolor: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <CardContent sx={{ p: 4 }}>
            {/* Main Content Layout: Sequences on Left, Content on Right */}
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              alignItems: 'flex-start',
              minHeight: '70vh',
              height: '70vh',
              position: 'relative'
            }}>
              {/* Left Sidebar - Sequences List */}
              <Box sx={{ 
                width: { xs: '100%', md: '300px' }, 
                flexShrink: 0,
                position: 'sticky',
                top: 24,
                alignSelf: 'flex-start',
                maxHeight: '70vh',
                zIndex: 10
              }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  bgcolor: '#F8F9FA',
                  border: '1px solid #E0E0E0',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '70vh',
                  overflowY: 'auto'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: '#2C3E50', fontWeight: 'bold' }}>
                        Séquences ({seance.sequences?.length || 0})
                      </Typography>
                      <Button
                        onClick={handleAddSequence}
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                          borderRadius: '20px',
                          px: 1.5,
                          py: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          minWidth: 'auto',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)'
                          }
                        }}
                      >
                        Ajouter
                      </Button>
                    </Box>
                    
                    {seance.sequences && seance.sequences.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {seance.sequences.map((sequence, index) => (
                          <Box 
                            key={sequence.id || `sequence_${index}`} 
                            sx={{ 
                              position: 'relative',
                              opacity: draggedSequenceIndex === index ? 0.5 : 1,
                              transition: 'all 0.2s',
                              ...(dragOverIndex === index && {
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: '-4px',
                                  top: 0,
                                  bottom: 0,
                                  width: '3px',
                                  backgroundColor: '#3498DB',
                                  borderRadius: '2px',
                                  zIndex: 1
                                }
                              })
                            }}
                            draggable={!editingSequenceIndex || editingSequenceIndex !== index}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                          >
                            {editingSequenceIndex === index ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.5, borderRadius: 2, bgcolor: '#FFFFFF', border: '1px solid #E0E0E0' }}>
                                <TextField
                                  value={editingSequenceTitle}
                                  onChange={(e) => setEditingSequenceTitle(e.target.value)}
                                  size="small"
                                  dir={isRTL ? 'rtl' : 'ltr'}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.85rem',
                                      height: '36px',
                                      borderRadius: '8px'
                                    }
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSaveSequenceTitle();
                                    } else if (e.key === 'Escape') {
                                      handleCancelEditSequenceTitle();
                                    }
                                  }}
                                  autoFocus
                                />
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                  <IconButton
                                    size="small"
                                    onClick={handleSaveSequenceTitle}
                                    sx={{ 
                                      color: '#27AE60',
                                      p: 0.5,
                                      '&:hover': { backgroundColor: '#E8F5E8' }
                                    }}
                                  >
                                    <SaveIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEditSequenceTitle}
                                    sx={{ 
                                      color: '#E74C3C',
                                      p: 0.5,
                                      '&:hover': { backgroundColor: '#FDF2F2' }
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                onClick={() => setActiveSequenceIndex(index)}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  cursor: editingSequenceIndex === index ? 'default' : 'grab',
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  ...(draggedSequenceIndex === index && {
                                    cursor: 'grabbing',
                                    opacity: 0.5
                                  }),
                                  ...(index === activeSequenceIndex ? {
                                    background: 'linear-gradient(135deg, #3498DB 0%, #9B59B6 100%)',
                                    color: '#FFFFFF',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 8px rgba(52, 152, 219, 0.3)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #2980B9 0%, #8E44AD 100%)',
                                      boxShadow: '0 4px 12px rgba(52, 152, 219, 0.4)'
                                    }
                                  } : {
                                    backgroundColor: '#F5F5F5',
                                    color: '#2C3E50',
                                    border: '1px solid #E0E0E0',
                                    '&:hover': {
                                      backgroundColor: '#EEEEEE',
                                      borderColor: '#BDBDBD'
                                    }
                                  }),
                                  '& .sequence-actions': {
                                    opacity: 0,
                                    transition: 'opacity 0.2s'
                                  },
                                  '&:hover .sequence-actions': {
                                    opacity: 1
                                  },
                                  '&:active': {
                                    cursor: 'grabbing'
                                  }
                                }}
                                title={editingSequenceIndex === index ? "En cours d'édition" : "Cliquez pour sélectionner ou glissez pour réorganiser"}
                              >
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: index === activeSequenceIndex ? 'bold' : 'normal',
                                    fontSize: '0.9rem',
                                    flex: 1,
                                    dir: isRTL ? 'rtl' : 'ltr'
                                  }}
                                >
                                  {index + 1}. {sequence.subTheme}
                                </Typography>
                                <Box 
                                  className="sequence-actions"
                                  sx={{ 
                                    display: 'flex', 
                                    gap: 0.5,
                                    alignItems: 'center'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenSequenceActionsMenu(e, index)}
                                    sx={{ 
                                      color: index === activeSequenceIndex ? '#FFFFFF' : '#6b7280',
                                      p: 0.5,
                                      '&:hover': { 
                                        backgroundColor: index === activeSequenceIndex ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
                                        color: index === activeSequenceIndex ? '#FFFFFF' : '#374151'
                                      }
                                    }}
                                    title="Actions"
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                  
                                  {/* Sequence actions menu */}
                                  <Menu
                                    anchorEl={sequenceActionsMenuAnchor}
                                    open={Boolean(sequenceActionsMenuAnchor) && sequenceActionsMenuIndex === index}
                                    onClose={handleCloseSequenceActionsMenu}
                                    anchorOrigin={{
                                      vertical: 'bottom',
                                      horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                      vertical: 'top',
                                      horizontal: 'right',
                                    }}
                                  >
                                    <MenuItem 
                                      onClick={() => {
                                        handleCloseSequenceActionsMenu();
                                        handleEditSequenceTitle(index);
                                      }}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                      <EditIcon sx={{ fontSize: '1.2rem', color: '#9B59B6' }} />
                                      <ListItemText primary="Éditer le titre" />
                                    </MenuItem>
                                    <MenuItem 
                                      onClick={() => {
                                        handleCloseSequenceActionsMenu();
                                        handleEditSequence();
                                      }}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                      <EditIcon sx={{ fontSize: '1.2rem', color: '#3498DB' }} />
                                      <ListItemText primary="Éditer le contenu" />
                                    </MenuItem>
                                    <MenuItem 
                                      onClick={() => {
                                        handleCloseSequenceActionsMenu();
                                      handleRemoveSequence(index);
                                    }}
                                    disabled={seance.sequences.length <= 1}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                      <DeleteIcon sx={{ fontSize: '1.2rem', color: seance.sequences.length <= 1 ? '#BDC3C7' : '#E74C3C' }} />
                                      <ListItemText primary="Supprimer" />
                                    </MenuItem>
                                  </Menu>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>

              {/* Right Content Area - Current Sequence */}
              <Box sx={{ 
                flex: 1, 
                minWidth: 0,
                height: '70vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 2
              }}>
                {activeSequence ? (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h4" sx={{ color: '#2C3E50', fontWeight: 'bold' }} dir={isRTL ? 'rtl' : 'ltr'}>
                        Séquence {activeSequenceIndex + 1}: {activeSequence.subTheme}
                      </Typography>
                    </Box>
                
                {/* blocs */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {activeSequence.blocs?.map((block, index) => (
                    <Box 
                      key={block.id || `block_${index}`} 
                      sx={{
                        '&:hover .add-block-button-container': {
                          opacity: 1,
                          pointerEvents: 'auto'
                        }
                      }}
                    >
                    <Box 
                      sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #E0E0E0',
                        overflow: 'visible',
                        transition: 'all 0.15s ease-in-out',
                        ...(movingBlockId === block.id && {
                          transform: 'scale(0.98)',
                          opacity: 0.7,
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                          border: '2px solid #3498DB'
                        }),
                        ...(newlyAddedBlocId === block.id && {
                          transform: 'scale(1.02)',
                          boxShadow: '0 8px 24px rgba(39, 174, 96, 0.3)',
                          border: '2px solid #27AE60',
                          animation: 'pulse 1s ease-in-out'
                        })
                      }}
                    >
                      {/* Block header */}
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#F8F9FA', 
                        borderBottom: '1px solid #E0E0E0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#6b7280' }}>
                            Bloc {index + 1}
                          </Typography>
                          <Box sx={{
                            px: 2,
                            py: 0.5,
                            backgroundColor: getBlockTypeColor(block.type_bloc).bg,
                            borderRadius: '12px',
                            border: `1px solid ${getBlockTypeColor(block.type_bloc).border}`
                          }}>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 'bold', 
                              color: getBlockTypeColor(block.type_bloc).text,
                              textTransform: 'uppercase'
                            }}>
                              {block.type_bloc}
                            </Typography>
                          </Box>
                          {/* Status display */}
                          {block.bloc_data?.status && (
                            <Chip
                              icon={block.bloc_data.status === 'text_validated' ? <CheckCircleIcon /> : undefined}
                              label={block.bloc_data.status === 'text_validated' ? 'Validé' : block.bloc_data.status}
                              color={block.bloc_data.status === 'text_validated' ? 'success' : 'default'}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                fontWeight: 'bold',
                                borderWidth: '2px'
                              }}
                            />
                          )}
                        </Box>
                        
                          {/* Block actions */}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {/* Block actions menu button */}
                            <IconButton
                              onClick={(e) => handleOpenBlockActionsMenu(e, index)}
                                sx={{ 
                                color: '#6b7280',
                                  '&:hover': {
                                  backgroundColor: '#f3f4f6',
                                  color: '#374151'
                                }
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            
                            {/* Block actions menu */}
                            <Menu
                              anchorEl={blockActionsMenuAnchor}
                              open={Boolean(blockActionsMenuAnchor) && blockActionsMenuBlockIndex === index}
                              onClose={handleCloseBlockActionsMenu}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                              }}
                            >
                              {/* Upload Image option - always shown */}
                              <MenuItem 
                                onClick={() => handleUploadImageClick(index)}
                                disabled={isUploadingImage}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: '200px'
                                }}
                              >
                                {isUploadingImage ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CloudUploadIcon sx={{ fontSize: '1.2rem', color: '#3498DB' }} />
                                )}
                                <ListItemText primary={isUploadingImage ? 'Upload...' : 'Uploader une image'} />
                              </MenuItem>
                              
                              {/* Generate Images option - only show for image, teacher_notes, quiz, sondage, or music blocks */}
                              {(block.type_bloc === 'image' || block.type_bloc === 'teacher_notes' || block.type_bloc === 'quiz' || block.type_bloc === 'sondage' || block.type_bloc === 'music') && (
                                <MenuItem 
                                  onClick={() => handleOpenImageGenerationFromMenu(block.id)}
                              sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: '200px'
                                  }}
                                >
                                  <ImageIcon sx={{ fontSize: '1.2rem', color: '#8E44AD' }} />
                                  <ListItemText primary="Générer les images" />
                                </MenuItem>
                              )}
                              
                              {/* Image Bank option - only show for image, teacher_notes, quiz, sondage, or music blocks */}
                              {(block.type_bloc === 'image' || block.type_bloc === 'teacher_notes' || block.type_bloc === 'quiz' || block.type_bloc === 'sondage' || block.type_bloc === 'music') && (
                                <MenuItem 
                                  onClick={() => handleOpenImageBank(index)}
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: '200px'
                                  }}
                                >
                                  <PhotoCameraIcon sx={{ fontSize: '1.2rem', color: '#27AE60' }} />
                                  <ListItemText primary="Banque d'image" />
                                </MenuItem>
                              )}
                              
                              {/* Modify with AI option - always shown */}
                              <MenuItem 
                              onClick={() => handleOpenAiBlocDialog(index)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: '200px'
                                }}
                              >
                                <SmartToyIcon sx={{ fontSize: '1.2rem', color: '#FF6B6B' }} />
                                <ListItemText primary="Modifier avec IA" />
                              </MenuItem>
                              
                              {/* Delete block option */}
                              <MenuItem 
                                onClick={() => {
                                  handleCloseBlockActionsMenu();
                                  setBlocToDelete({ sequenceIndex: activeSequenceIndex, blocIndex: index });
                                  setDeleteBlocDialogOpen(true);
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: '200px'
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: '1.2rem', color: '#E74C3C' }} />
                                <ListItemText primary="Supprimer le bloc" />
                              </MenuItem>
                            </Menu>
                            <Button 
                              size="small"
                              disabled={index === 0}
                              sx={{ 
                                color: index === 0 ? '#BDC3C7' : '#3498DB',
                                fontSize: '0.8rem',
                                minWidth: 'auto',
                                px: 1,
                                py: 0.5,
                                borderRadius: '4px',
                                backgroundColor: index === 0 ? '#F8F9FA' : '#E8F4FD',
                                border: `1px solid ${index === 0 ? '#E0E0E0' : '#BBDEFB'}`,
                                '&:hover': index === 0 ? {} : {
                                  backgroundColor: '#D1ECF1',
                                  borderColor: '#3498DB',
                                  transform: 'scale(1.05)'
                                },
                                '&:active': index === 0 ? {} : {
                                  transform: 'scale(0.95)'
                                }
                              }}
                              onClick={() => handleMoveBloc(activeSequenceIndex, block.id, 'up')}
                              title={index === 0 ? "Premier bloc - ne peut pas être déplacé vers le haut" : "Déplacer vers le haut"}
                            >
                              ↑
                            </Button>
                            <Button 
                              size="small"
                              disabled={index === activeSequence.blocs.length - 1}
                              sx={{ 
                                color: index === activeSequence.blocs.length - 1 ? '#BDC3C7' : '#3498DB',
                                fontSize: '0.8rem',
                                minWidth: 'auto',
                                px: 1,
                                py: 0.5,
                                borderRadius: '4px',
                                backgroundColor: index === activeSequence.blocs.length - 1 ? '#F8F9FA' : '#E8F4FD',
                                border: `1px solid ${index === activeSequence.blocs.length - 1 ? '#E0E0E0' : '#BBDEFB'}`,
                                '&:hover': index === activeSequence.blocs.length - 1 ? {} : {
                                  backgroundColor: '#D1ECF1',
                                  borderColor: '#3498DB',
                                  transform: 'scale(1.05)'
                                },
                                '&:active': index === activeSequence.blocs.length - 1 ? {} : {
                                  transform: 'scale(0.95)'
                                }
                              }}
                              onClick={() => handleMoveBloc(activeSequenceIndex, block.id, 'down')}
                              title={index === activeSequence.blocs.length - 1 ? "Dernier bloc - ne peut pas être déplacé vers le bas" : "Déplacer vers le bas"}
                            >
                              ↓
                            </Button>
                          </Box>
                      </Box>
                      
                      {/* Dynamic block rendering */}
                      <DynamicBlockRenderer 
                        block={{
                          id: block.id,
                          type: block.type_bloc,
                          bloc_data: block.bloc_data,
                          bloc_index: block.bloc_index,
                          sequence: activeSequence
                        }}
                        sessionId={seanceId}
                        sequenceIndex={activeSequenceIndex}
                        onSave={handleSaveBlock}
                        onRefresh={updateSeance}
                        isEditable={true}
                        isRTL={isRTL}
                        openRegenerateDialogForBlock={
                          openRegenerateDialogForBlock?.blockId === block.id 
                            ? openRegenerateDialogForBlock 
                            : null
                        }
                        onRegenerateDialogOpened={() => setOpenRegenerateDialogForBlock(null)}
                      />
                    </Box>
                    
                    {/* Add block button - appears on hover below the block */}
                    <Box
                      className="add-block-button-container"
                      sx={{
                        width: '100%',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none',
                        mt: -1.5
                      }}
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          const event = {
                            currentTarget: e.currentTarget
                          };
                          setBlocMenuAnchor(event.currentTarget);
                          // Store the index where to insert the new block
                          setBlocInsertIndex(index + 1);
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                          color: 'white',
                          width: 40,
                          height: 40,
                          pointerEvents: 'auto',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(39, 174, 96, 0.3)'
                        }}
                        title="Ajouter un bloc ici"
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    </Box>
                  )) || (
                    <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: '#7F8C8D', mb: 2 }}>
                          Aucun bloc généré pour cette séquence
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<PlayIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                            borderRadius: '20px',
                            px: 3,
                            py: 1,
                            fontSize: '1rem',
                            fontWeight: 'bold'
                          }}
                        >
                          Générer les blocs
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            ) : (
              <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: '#7F8C8D', mb: 2 }}>
                    Aucune séquence disponible
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#7F8C8D', mb: 3 }}>
                    Cette séance n'a pas encore de séquences générées.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PlayIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                      borderRadius: '20px',
                      px: 3,
                      py: 1,
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    Générer les séquences
                  </Button>
                </CardContent>
              </Card>
            )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Floating Action Buttons */}
        <Box sx={{ 
          position: 'fixed', 
          bottom: 24, 
          right: aiChatDialogOpen ? `${DRAWER_WIDTH + 24}px` : 24,
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          transition: 'right 0.3s ease-in-out',
          zIndex: 1000
        }}>
          <Fab
            color="primary"
            aria-label="AI chat"
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                transform: 'scale(1.1)'
              }
            }}
            onClick={() => setAiChatDialogOpen(!aiChatDialogOpen)}
            title={aiChatDialogOpen ? "Fermer le chat" : "Chat avec l'IA"}
          >
            <SmartToyIcon />
          </Fab>
          <Fab
            color="primary"
            aria-label="regenerate seance"
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #C2185B 0%, #AD1457 100%)',
                transform: 'scale(1.1)'
              }
            }}
            onClick={() => setChatDialogOpen(true)}
            title="Régénérer la séance avec un prompt"
          >
            <ChatIcon />
          </Fab>
          <Fab
            color="primary"
            aria-label="add block"
            disabled={!activeSequence}
            sx={{
              background: activeSequence 
                ? 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)'
                : 'linear-gradient(135deg, #BDC3C7 0%, #95A5A6 100%)',
              '&:hover': activeSequence ? {
                background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                transform: 'scale(1.1)'
              } : {},
              '&:disabled': {
                background: 'linear-gradient(135deg, #BDC3C7 0%, #95A5A6 100%)',
                color: '#7F8C8D'
              }
            }}
            onClick={handleBlocMenuOpen}
            title={activeSequence ? "Ajouter un bloc à la séquence" : "Sélectionnez d'abord une séquence"}
          >
            <AddIcon />
          </Fab>
          <Fab
            color="secondary"
            aria-label="edit seance"
            sx={{
              background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                transform: 'scale(1.1)'
              }
            }}
            onClick={handleEditSequence}
          >
            <EditIcon />
          </Fab>
        </Box>

        {/* Edit Sequence Dialog */}
        <Dialog 
          open={editSequenceDialogOpen} 
          onClose={handleCancelEditCurrentSequence}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 1,
            background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
            color: 'white',
            borderRadius: '16px 16px 0 0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Éditer la séquence
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
              Modifiez la séquence actuellement sélectionnée avec un prompt détaillé.
            </Typography>
            
            {activeSequence && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#F8F9FA', borderRadius: '12px', border: '1px solid #E0E0E0' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2C3E50', mb: 1 }}>
                  Séquence actuelle
                </Typography>
                <Typography variant="h6" sx={{ color: '#3498DB', fontWeight: 'bold' }} dir={isRTL ? 'rtl' : 'ltr'}>
                  {activeSequence.subTheme}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                Instructions pour modifier la séquence
              </Typography>
              
              <TextField
                fullWidth
                label="Prompt détaillé"
                value={editSequencePrompt}
                onChange={(e) => setEditSequencePrompt(e.target.value)}
                placeholder="Ex: Changez le style des images pour être plus modernes et colorées, ajoutez plus d'interactivité avec des quiz, simplifiez le langage pour des enfants plus jeunes, incluez plus d'histoires personnelles..."
                multiline
                rows={6}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px'
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    handleSubmitEditCurrentSequence();
                  }
                }}
              />
              
              <Typography variant="caption" sx={{ mt: 1, color: '#9ca3af', display: 'block' }}>
                💡 Soyez spécifique dans vos instructions pour obtenir de meilleurs résultats. Utilisez Ctrl+Entrée pour soumettre rapidement.
              </Typography>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleCancelEditCurrentSequence}
              variant="outlined"
              sx={{
                borderColor: '#6b7280',
                color: '#6b7280',
                borderRadius: '20px',
                px: 3,
                py: 1,
                fontWeight: 'bold'
              }}
            >
              Annuler
            </Button>
            
            <Button
              onClick={handleSubmitEditCurrentSequence}
              variant="contained"
              disabled={!editSequencePrompt.trim()}
              sx={{
                background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                borderRadius: '20px',
                px: 3,
                py: 1,
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)'
                },
                '&:disabled': {
                  background: '#BDBDBD'
                }
              }}
            >
              Modifier la séquence
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
        </Box>
        
        {/* AI Chat Sidebar */}
        <AIChat
          open={aiChatDialogOpen}
          onClose={() => setAiChatDialogOpen(false)}
          seanceId={seanceId}
        />
      </Box>
      
      {/* Sequence Creation Dialog */}
      <Dialog 
        open={sequenceDialogOpen} 
        onClose={handleCancelSequenceDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Créer une nouvelle séquence
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 6 }}>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Titre de la séquence"
              value={newSequenceTitle}
              onChange={(e) => setNewSequenceTitle(e.target.value)}
              placeholder="Ex: Introduction aux traditions de Shabbat"
              fullWidth
              required
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 2, color: '#6b7280' }}>
            Entrez simplement un titre. Vous pourrez ajouter du contenu ensuite.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelSequenceDialog}
            variant="outlined"
            sx={{
              borderColor: '#6b7280',
              color: '#6b7280',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold'
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleCreateSequence}
            variant="contained"
            disabled={!newSequenceTitle.trim()}
            sx={{
              background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)'
              },
              '&:disabled': {
                background: '#BDBDBD'
              }
            }}
          >
            Créer la séquence
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Sequence Edit Dialog */}
      <Dialog 
        open={regenerateSequenceDialogOpen} 
        onClose={handleCancelEditSequenceTitle}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Éditer la séquence
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
            Modifiez le titre de la séquence et/ou régénérez son contenu avec de nouvelles instructions.
          </Typography>
          
          {regenerateSequenceIndex !== null && (
            <Typography variant="h6" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }} dir={isRTL ? 'rtl' : 'ltr'}>
              Séquence actuelle: {seance.sequences[regenerateSequenceIndex]?.subTheme}
            </Typography>
          )}
          
          {/* Title Edit Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#2C3E50', fontWeight: 'bold' }}>
              Titre de la séquence
            </Typography>
            <TextField
              fullWidth
              label="Nouveau titre"
              value={editSequenceTitle}
              onChange={(e) => setEditSequenceTitle(e.target.value)}
              placeholder="Ex: Introduction aux traditions de Shabbat"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
            />
          </Box>
          
          {/* Content Regeneration Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#2C3E50', fontWeight: 'bold' }}>
              Régénération du contenu (optionnel)
            </Typography>
            <TextField
              fullWidth
              label="Instructions pour régénérer le contenu"
              value={regenerateSequencePrompt}
              onChange={(e) => setRegenerateSequencePrompt(e.target.value)}
              placeholder="Ex: Changez le style des images pour être plus modernes, ajoutez plus d'interactivité, simplifiez le langage..."
              multiline
              rows={4}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSaveSequenceTitle();
                }
              }}
            />
            <Typography variant="caption" sx={{ mt: 1, color: '#9ca3af', display: 'block' }}>
              💡 Laissez vide si vous ne voulez que changer le titre. Soyez spécifique pour de meilleurs résultats.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelEditSequenceTitle}
            variant="outlined"
            sx={{
              borderColor: '#6b7280',
              color: '#6b7280',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold'
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleSaveSequenceTitle}
            variant="contained"
            disabled={!editSequenceTitle.trim()}
            sx={{
              background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #8E44AD 0%, #7D3C98 100%)'
              },
              '&:disabled': {
                background: '#BDBDBD'
              }
            }}
          >
            {regenerateSequencePrompt.trim() ? 'Éditer et régénérer' : 'Mettre à jour le titre'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bloc Selection Menu */}
      <Menu
        anchorEl={blocMenuAnchor}
        open={Boolean(blocMenuAnchor)}
        onClose={handleBlocMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            minWidth: 200,
            maxWidth: 250
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
            Ajouter un bloc
          </Typography>
        </Box>
        
        {availableBlocTypes.map((blocType) => (
          <MenuItem
            key={blocType.type}
            onClick={() => handleAddBloc(blocType.type)}
            sx={{
              py: 1,
              px: 1.5,
              borderRadius: '6px',
              mx: 0.5,
              my: 0.25,
              '&:hover': {
                backgroundColor: '#F8F9FA',
                transform: 'scale(1.01)',
                transition: 'all 0.15s ease'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Box
                sx={{
                  p: 0.5,
                  borderRadius: '6px',
                  backgroundColor: `${blocType.color}15`,
                  color: blocType.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {blocType.icon}
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                  {blocType.label}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Chat Dialog for Seance Regeneration */}
      <Dialog 
        open={chatDialogOpen}
        onClose={handleCloseChatDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Régénérer la séance
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#2C3E50' }}>
            Écrivez un prompt pour régénérer entièrement cette séance. L'IA créera une nouvelle version basée sur vos instructions.
          </Typography>
          
          {regenerationError && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: '#FFEBEE', 
              borderRadius: '8px',
              border: '1px solid #F44336'
            }}>
              <Typography variant="body2" sx={{ color: '#D32F2F', fontWeight: 'bold', mb: 1 }}>
                ⚠️ {regenerationError}
              </Typography>
              {retryCount < 2 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRetryRegeneration}
                  disabled={isRegeneratingSeance}
                  sx={{
                    borderColor: '#F44336',
                    color: '#D32F2F',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1,
                    '&:hover': {
                      backgroundColor: '#FFEBEE',
                      borderColor: '#D32F2F'
                    }
                  }}
                >
                  Réessayer ({retryCount + 1}/3)
                </Button>
              )}
            </Box>
          )}
          
          <TextField
            label="Prompt de régénération"
            value={seancePrompt}
            onChange={(e) => {
              setSeancePrompt(e.target.value);
              if (regenerationError) setRegenerationError(null); // Clear error when user types
            }}
            placeholder="Ex: Changez le style pour être plus moderne, ajoutez plus d'interactivité, simplifiez le langage pour des enfants plus jeunes, incluez plus d'histoires personnelles..."
            multiline
            rows={6}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseChatDialog}
            variant="outlined"
            sx={{
              borderColor: '#d1d5db',
              color: '#6b7280',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold'
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleRegenerateSeance}
            variant="contained"
            disabled={!seancePrompt.trim() || isRegeneratingSeance}
            startIcon={isRegeneratingSeance ? <CircularProgress size={20} color="inherit" /> : <ChatIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #C2185B 0%, #AD1457 100%)'
              },
              '&:disabled': {
                background: '#BDBDBD'
              }
            }}
          >
            {isRegeneratingSeance ? 'Régénération...' : 'Régénérer la séance'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Bloc Modification Dialog */}
      <Dialog 
        open={aiBlocDialogOpen}
        onClose={handleCloseAiBlocDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            ✨ Modification IA du Bloc
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
            Entrez vos instructions pour modifier ce bloc avec l'intelligence artificielle.
            Soyez spécifique dans vos demandes pour obtenir de meilleurs résultats.
          </Typography>
          
          <TextField
            fullWidth
            label="Instructions de modification"
            value={aiBlocPrompt}
            onChange={(e) => setAiBlocPrompt(e.target.value)}
            placeholder="Ex: Changez les couleurs pour être plus vives, ajoutez plus d'interactivité, simplifiez le langage pour des enfants plus jeunes, incluez plus d'exemples concrets..."
            multiline
            rows={6}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleModifyBloc();
              }
            }}
          />
          
          <Typography variant="caption" sx={{ mt: 1, color: '#9ca3af', display: 'block' }}>
            💡 Soyez spécifique dans vos instructions pour obtenir de meilleurs résultats. Utilisez Ctrl+Entrée pour soumettre rapidement.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseAiBlocDialog}
            variant="outlined"
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleModifyBloc}
            variant="contained"
            disabled={!aiBlocPrompt.trim() || isModifyingBloc}
            startIcon={isModifyingBloc ? <CircularProgress size={20} color="inherit" /> : <span>✨</span>}
            sx={{
              background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #8E44AD 0%, #7D3C98 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(155, 89, 182, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isModifyingBloc ? 'Modification...' : 'Modifier avec l\'IA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Generation Dialog */}
      <Dialog 
        open={imageGenerationDialogOpen}
        onClose={handleCloseImageGenerationDialog}
        maxWidth={imageGenerationAiChatOpen ? false : "md"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            },
            maxWidth: imageGenerationAiChatOpen ? `calc(100% - 64px)` : '900px',
            width: imageGenerationAiChatOpen ? `calc(900px + ${DRAWER_WIDTH}px)` : 'auto',
            transition: 'width 0.3s ease, max-width 0.3s ease'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'linear-gradient(135deg, #8E44AD 0%, #9B59B6 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon />
            Générer les images
          </Typography>
            <Button
              onClick={() => setImageGenerationAiChatOpen(!imageGenerationAiChatOpen)}
              variant={imageGenerationAiChatOpen ? "contained" : "outlined"}
              startIcon={<SmartToyIcon />}
              size="small"
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                ...(imageGenerationAiChatOpen && {
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                  }
                })
              }}
            >
              Chat IA
            </Button>
          </Box>
          <IconButton
            onClick={handleCloseImageGenerationDialog}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          display: 'flex',
          gap: 2,
          p: 3,
          position: 'relative',
          overflow: 'hidden',
          height: 'calc(100vh - 300px)',
          maxHeight: 'calc(100vh - 300px)',
          minHeight: '450px'
        }}>
          <Box sx={{ 
            flex: 1, 
            minWidth: 0,
            transition: 'flex 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1,
            height: '100%',
            maxHeight: '100%'
          }}>
          {/* Model, Style, Aspect ratio, and Number of variations - All on same row - First row */}
          <Grid container spacing={2} sx={{ mb: 3, pt: 3 }}>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel id="image-model-label">Modèle de génération d'images</InputLabel>
                <Select
                  labelId="image-model-label"
                  value={selectedImageGenerationModel}
                  label="Modèle de génération d'images"
                  onChange={(e) => setSelectedImageGenerationModel(e.target.value)}
                  disabled={modelsLoading}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  {modelsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Chargement des modèles...
                    </MenuItem>
                  ) : imageGenerationModels.length > 0 ? (
                    imageGenerationModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {model.name}
                        </Typography>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Aucun modèle disponible</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel id="image-style-label">Style</InputLabel>
                <Select
                  labelId="image-style-label"
                  value={imageGenerationStyle}
                  label="Style"
                  onChange={(e) => setImageGenerationStyle(e.target.value)}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  <MenuItem value="cartoon">🎨 Cartoon</MenuItem>
                  <MenuItem value="realistic">📸 Réaliste</MenuItem>
                  <MenuItem value="semi-realistic">🎭 Semi-réaliste</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel id="aspect-ratio-label">Ratio d'aspect</InputLabel>
                <Select
                  labelId="aspect-ratio-label"
                  value={imageGenerationAspectRatio}
                  label="Ratio d'aspect"
                  onChange={(e) => setImageGenerationAspectRatio(e.target.value)}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  <MenuItem value="16:9">16:9 (Paysage)</MenuItem>
                  <MenuItem value="1:1">1:1 (Carré)</MenuItem>
                  <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                  <MenuItem value="4:3">4:3 (Classique)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel id="variations-label">Nombre de variations</InputLabel>
                <Select
                  labelId="variations-label"
                  value={imageGenerationNbVariations}
                  label="Nombre de variations"
                  onChange={(e) => setImageGenerationNbVariations(e.target.value)}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num} variation{num > 1 ? 's' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Block Information Section */}
          {(() => {
            if (!imageGenerationBlockId || !activeSequence?.blocs) return null;
            const block = activeSequence.blocs.find(b => b.id === imageGenerationBlockId);
            if (!block) return null;
            
            const blockType = block.type_bloc || block.bloc_type;
            
            // Quiz block: Show question and answers
            if (blockType === 'quiz') {
              const question = block.bloc_data?.question || block.content?.question?.text || block.content?.question || '';
              const answers = block.bloc_data?.answers || block.content?.answers || [];
              
              if (!question && answers.length === 0) return null;
              
              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2, color: '#34495E' }}>
                    Informations du quiz
                  </Typography>
                  <Box sx={{ 
                    padding: '16px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px', 
                    border: '1px solid #e1e8ed' 
                  }}>
                    {/* Question */}
                    {question && (
                      <Box sx={{ mb: 2 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: '#2C3E50',
                            marginBottom: '8px'
                          }}
                        >
                          Question:
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#2C3E50', 
                            lineHeight: 1.6,
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e1e8ed'
                          }}
                        >
                          {question}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Answers */}
                    {answers.length > 0 && (
                      <Box>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: '#2C3E50',
                            marginBottom: '8px'
                          }}
                        >
                          Réponses:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {answers.map((answer, index) => {
                            const answerText = answer.answer || answer.text || '';
                            const isCorrect = answer.outcome === 'good answer' || answer.is_correct || false;
                            
                            return (
                              <Box
                                key={answer.answer_index !== undefined ? answer.answer_index : index}
                                sx={{
                                  padding: '12px',
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: `2px solid ${isCorrect ? '#10b981' : '#e1e8ed'}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                {isCorrect && (
                                  <CheckCircle sx={{ color: '#10b981', fontSize: '20px' }} />
                                )}
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#2C3E50', 
                                    lineHeight: 1.6,
                                    flex: 1
                                  }}
                                >
                                  {answerText || `Réponse ${index + 1}`}
                                </Typography>
                                {isCorrect && (
                                  <Chip 
                                    label="Correcte" 
                                    size="small" 
                                    sx={{ 
                                      backgroundColor: '#10b981', 
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }} 
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }
            
            // Teacher notes block: Show notes
            const notes = block.bloc_data?.notes || block.content?.notes || block.layers || [];
            if (notes.length === 0) return null;
            
            return (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2, color: '#34495E' }}>
                  Informations du bloc
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {notes.map((note, index) => {
                    // Handle both new API structure (bloc_data.notes) and old structure (layers)
                    const noteData = note.title ? note : {
                      id: `note_${index}`,
                      title: note.title || `Note ${index + 1}`,
                      content: { text: note.content || note.text || 'No content generated yet' }
                    };
                    
                    return (
                      <Box
                        key={noteData.id || index}
                        sx={{
                          padding: '16px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e1e8ed'
                        }}
                      >
                        {/* Note header */}
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: '#2C3E50',
                            marginBottom: '12px'
                          }}
                        >
                          {noteData.title}
                        </Typography>
                        
                        {/* Note content */}
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#2C3E50', 
                            lineHeight: 1.6,
                            marginBottom: '8px'
                          }}
                        >
                          {noteData.content?.text || noteData.content || 'No content generated yet'}
                        </Typography>
                        
                        {/* Character count */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          <span>
                            {(noteData.content?.text || noteData.content || '').length} characters
                          </span>
                          <span style={{
                            color: (noteData.content?.text || noteData.content || '').length > 500 ? '#dc2626' : '#6b7280',
                            fontWeight: (noteData.content?.text || noteData.content || '').length > 500 ? 'bold' : 'normal'
                          }}>
                            Max: 500
                          </span>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}

          <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
            Entrez un prompt pour générer des images pour ce bloc. Vous pouvez spécifier le style, les éléments visuels, et autres détails.
          </Typography>
          
          {/* Prompt field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Prompt de génération d'images"
              value={imageGenerationPrompt}
              onChange={(e) => setImageGenerationPrompt(e.target.value)}
              placeholder="Ex: Images en style Pixar montrant des enfants autour d'une table de Shabbat, couleurs chaleureuses, ambiance joyeuse..."
              multiline
              rows={7}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  border: '2px solid #000'
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleGenerateImages();
                }
              }}
            />
          </Box>
          
          <Typography variant="caption" sx={{ mt: 1, color: '#9ca3af', display: 'block' }}>
            💡 Soyez spécifique dans votre prompt pour obtenir de meilleurs résultats. Utilisez Ctrl+Entrée pour soumettre rapidement.
          </Typography>
          </Box>
          
          {/* AI Chat Sidebar */}
          {imageGenerationAiChatOpen && (
            <Box sx={{ 
              width: DRAWER_WIDTH, 
              flexShrink: 0,
              borderLeft: '1px solid #e0e0e0',
              ml: 2,
              height: '100%',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                maxHeight: '100%',
                '& > div': {
                  position: 'relative !important',
                  top: '0 !important',
                  height: '100% !important',
                  maxHeight: '100% !important',
                  flex: '1 1 auto !important',
                  overflow: 'hidden !important',
                  // Override AIChat root Box styles
                  '&:first-of-type': {
                    height: '100% !important',
                    maxHeight: '100% !important',
                    display: 'flex !important',
                    flexDirection: 'column !important',
                    boxShadow: 'none !important',
                    borderLeft: 'none !important',
                    position: 'relative !important',
                    alignSelf: 'auto !important'
                  }
                }
              }}>
                <AIChat
                  open={imageGenerationAiChatOpen}
                  onClose={() => setImageGenerationAiChatOpen(false)}
                  seanceId={seanceId}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseImageGenerationDialog}
            variant="outlined"
            disabled={isGeneratingImages}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleGenerateImages}
            variant="contained"
            disabled={!imageGenerationPrompt.trim() || isGeneratingImages || !selectedImageGenerationModel}
            startIcon={isGeneratingImages ? <CircularProgress size={20} color="inherit" /> : <ImageIcon />}
            sx={{
              background: 'linear-gradient(135deg, #8E44AD 0%, #9B59B6 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #7D3C98 0%, #8E44AD 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(142, 68, 173, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isGeneratingImages ? 'Génération...' : 'Générer les images'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Generation Dialog for Sequence (when adding image block) */}
      <Dialog
        open={sequenceImageGenerationDialogOpen}
        onClose={() => setSequenceImageGenerationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            minHeight: '600px',
            '& .MuiDialog-paper': {
              borderRadius: '20px',
              minHeight: '600px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '24px', 
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #8E44AD 0%, #9B59B6 100%)',
          color: 'white',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          Générer des images pour la séquence
          <IconButton
            onClick={() => setSequenceImageGenerationDialogOpen(false)}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          {/* Choices in one row at the top */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Model selection */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="sequence-image-model-label">Modèle</InputLabel>
                <Select
                  labelId="sequence-image-model-label"
                  value={selectedSequenceImageGenerationModel}
                  label="Modèle"
                  onChange={(e) => setSelectedSequenceImageGenerationModel(e.target.value)}
                  disabled={modelsLoading}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  {modelsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Chargement...
                    </MenuItem>
                  ) : imageGenerationModels.length > 0 ? (
                    imageGenerationModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {model.name}
                        </Typography>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Aucun modèle disponible</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Style selection */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="sequence-image-style-label">Style</InputLabel>
                <Select
                  labelId="sequence-image-style-label"
                  value={sequenceImageGenerationStyle}
                  label="Style"
                  onChange={(e) => setSequenceImageGenerationStyle(e.target.value)}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  <MenuItem value="cartoon">Cartoon</MenuItem>
                  <MenuItem value="pixar">Pixar</MenuItem>
                  <MenuItem value="realistic">Réaliste</MenuItem>
                  <MenuItem value="drawing">Dessin</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Aspect ratio selection */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="sequence-aspect-ratio-label">Ratio</InputLabel>
                <Select
                  labelId="sequence-aspect-ratio-label"
                  value={sequenceImageGenerationAspectRatio}
                  label="Ratio"
                  onChange={(e) => setSequenceImageGenerationAspectRatio(e.target.value)}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  <MenuItem value="16:9">16:9 (Paysage)</MenuItem>
                  <MenuItem value="1:1">1:1 (Carré)</MenuItem>
                  <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                  <MenuItem value="4:3">4:3 (Classique)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Number of variations */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="sequence-variations-label">Variations</InputLabel>
                <Select
                  labelId="sequence-variations-label"
                  value={sequenceImageGenerationNbVariations}
                  label="Variations"
                  onChange={(e) => setSequenceImageGenerationNbVariations(e.target.value)}
                  sx={{
                    borderRadius: '12px'
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num} {num === 1 ? 'image' : 'images'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Prompt input */}
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Prompt de génération d'images"
              value={sequenceImageGenerationPrompt}
              onChange={(e) => setSequenceImageGenerationPrompt(e.target.value)}
              fullWidth
              multiline
              rows={14}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleGenerateImagesForSequence();
                }
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={() => setSequenceImageGenerationDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleGenerateImagesForSequence}
            variant="contained"
            disabled={!sequenceImageGenerationPrompt.trim() || isGeneratingSequenceImages || !selectedSequenceImageGenerationModel}
            startIcon={isGeneratingSequenceImages ? <CircularProgress size={20} color="inherit" /> : <ImageIcon />}
            sx={{
              background: 'linear-gradient(135deg, #8E44AD 0%, #9B59B6 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #7D3C98 0%, #8E44AD 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(142, 68, 173, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isGeneratingSequenceImages ? 'Génération...' : 'Générer les images'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Bank Dialog */}
      <Dialog
        open={imageBankDialogOpen}
        onClose={handleCloseImageBank}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: '2px solid #27AE60'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          background: 'linear-gradient(135deg, #27AE60 0%, #229954 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoCameraIcon />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Banque d'images
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {loadingGalleryImages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : galleryImages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PhotoCameraIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune image disponible
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Veuillez ajouter des images via l'écran "Galerie d'Images"
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {galleryImages.map((image) => (
                <Grid item xs={12} sm={6} md={4} key={image.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 3,
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      border: selectedGalleryImages.has(image.id) ? '3px solid #27AE60' : '1px solid #E0E0E0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                    onClick={() => handleToggleGalleryImageSelection(image.id)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        image={image.url}
                        alt={image.name || 'Image'}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover'
                        }}
                      />
                      <Checkbox
                        checked={selectedGalleryImages.has(image.id)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          '&.Mui-checked': {
                            color: '#27AE60'
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleGalleryImageSelection(image.id);
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                        {image.name || 'Sans titre'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseImageBank}
            disabled={isSavingBlock}
            sx={{ borderRadius: '20px', textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleImportGalleryImages}
            variant="contained"
            disabled={selectedGalleryImages.size === 0 || isSavingBlock || loadingGalleryImages}
            startIcon={isSavingBlock ? <CircularProgress size={20} color="inherit" /> : <PhotoCameraIcon />}
            sx={{
              bgcolor: '#27AE60',
              borderRadius: '20px',
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#229954'
              }
            }}
          >
            {isSavingBlock ? 'Importation...' : `Importer ${selectedGalleryImages.size} image${selectedGalleryImages.size > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Sequence Confirmation Dialog */}
      <Dialog
        open={deleteSequenceDialogOpen}
        onClose={() => {
          setDeleteSequenceDialogOpen(false);
          setSequenceToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningIcon />
            Confirmation de suppression
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2C3E50' }}>
            ⚠️ Attention
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#6b7280' }}>
            Êtes-vous sûr de vouloir supprimer cette séquence ?
          </Typography>
          {sequenceToDelete !== null && seance.sequences[sequenceToDelete] && (
            <Typography variant="body2" sx={{ color: '#E53935', fontWeight: 'bold' }} dir={isRTL ? 'rtl' : 'ltr'}>
              {seance.sequences[sequenceToDelete].subTheme}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 2, color: '#9ca3af' }}>
            Cette action est irréversible. Tous les blocs de cette séquence seront définitivement supprimés.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => {
              setDeleteSequenceDialogOpen(false);
              setSequenceToDelete(null);
            }}
            variant="outlined"
            disabled={isDeletingSequence}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleConfirmDeleteSequence}
            variant="contained"
            disabled={isDeletingSequence}
            startIcon={isDeletingSequence ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(229, 57, 53, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isDeletingSequence ? 'Suppression...' : 'Confirmer la suppression'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Bloc Confirmation Dialog */}
      <Dialog
        open={deleteBlocDialogOpen}
        onClose={() => !isDeletingBloc && setDeleteBlocDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningIcon />
            Confirmation de suppression
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2C3E50' }}>
            ⚠️ Attention
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#6b7280' }}>
            Êtes-vous sûr de vouloir supprimer ce bloc ?
          </Typography>
          {(() => {
            if (blocToDelete.sequenceIndex !== null && blocToDelete.blocIndex !== null && seance?.sequences?.[blocToDelete.sequenceIndex]?.blocs?.[blocToDelete.blocIndex]) {
              const block = seance.sequences[blocToDelete.sequenceIndex].blocs[blocToDelete.blocIndex];
              const blockType = block.type_bloc || 'bloc';
              return (
                <Typography variant="body2" sx={{ color: '#E53935', fontWeight: 'bold' }}>
                  Type: {blockType}
                </Typography>
              );
            }
            return null;
          })()}
          <Typography variant="body2" sx={{ mt: 2, color: '#9ca3af' }}>
            Cette action est irréversible. Le bloc sera définitivement supprimé de la séquence.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setDeleteBlocDialogOpen(false)}
            variant="outlined"
            disabled={isDeletingBloc}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleConfirmDeleteBloc}
            variant="contained"
            disabled={isDeletingBloc}
            startIcon={isDeletingBloc ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(229, 57, 53, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isDeletingBloc ? 'Suppression...' : 'Confirmer la suppression'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningIcon />
            Confirmation de suppression
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2C3E50' }}>
            ⚠️ Attention
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#6b7280' }}>
            Êtes-vous sûr de vouloir supprimer cette séance ?
          </Typography>
          <Typography variant="body2" sx={{ color: '#E53935', fontWeight: 'bold' }}>
            {seance?.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: '#9ca3af' }}>
            Cette action est irréversible. Toutes les données de cette séance seront définitivement supprimées.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            disabled={isDeleting}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleDeleteSeance}
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(229, 57, 53, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isDeleting ? 'Suppression...' : 'Confirmer la suppression'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Error Dialog */}
      <Dialog
        open={uploadErrorDialogOpen}
        onClose={() => setUploadErrorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningIcon sx={{ fontSize: '2rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Erreur d'upload
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#2C3E50', textAlign: 'center' }}>
            {uploadErrorMessage}
          </Typography>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#FFEBEE', 
            borderRadius: '8px',
            border: '1px solid #F44336'
          }}>
            <Typography variant="body2" sx={{ color: '#D32F2F', textAlign: 'center' }}>
              Veuillez vérifier que l'image est valide et réessayer.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
          <Button
            onClick={() => setUploadErrorDialogOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
              borderRadius: '25px',
              px: 4,
              py: 1,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(229, 57, 53, 0.4)'
              }
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thumbnail Selection Dialog */}
      <Dialog
        open={thumbnailDialogOpen}
        onClose={handleCloseThumbnailDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: '2px solid #ec4899'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontSize: '24px', 
          fontWeight: 'bold',
          pb: 2,
          color: '#ec4899'
        }}>
          Sélectionner une miniature
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
          {(() => {
            console.log('Rendering dialog, allSeanceImages:', allSeanceImages.length, allSeanceImages);
            return null;
          })()}
          {allSeanceImages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ImageIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucune image disponible dans cette séance
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
                Ajoutez des images aux blocs pour pouvoir sélectionner une miniature
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, fontSize: '0.7rem', fontStyle: 'italic' }}>
                (Vérifiez la console pour les détails de débogage)
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {allSeanceImages.length} image{allSeanceImages.length > 1 ? 's' : ''} trouvée{allSeanceImages.length > 1 ? 's' : ''} - Cliquez sur une image pour la sélectionner
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                {allSeanceImages.map((imageData, index) => {
                  console.log('Rendering image:', index, imageData.url);
                  return (
                <Grid item xs={12} sm={6} md={4} key={`${imageData.sequenceIndex}-${imageData.blockIndex}-${imageData.imageIndex}`} sx={{ display: 'flex' }}>
                  <Box
                    onClick={() => handleSetThumbnail(imageData.imageObject)}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '3px solid #e5e7eb',
                      transition: 'all 0.3s ease',
                      bgcolor: '#fff',
                      width: '100%',
                      minHeight: '200px',
                      '&:hover': {
                        borderColor: '#ec4899',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(236, 72, 153, 0.3)'
                      }
                    }}
                  >
                    <Box sx={{ 
                      position: 'relative', 
                      width: '100%', 
                      height: '200px',
                      bgcolor: '#F5F5F5',
                      overflow: 'hidden',
                      borderRadius: '8px'
                    }}>
                      <Box
                        component="img"
                        src={imageData.url}
                        alt={`Image ${index + 1} - ${imageData.sequenceTitle}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', imageData.url, imageData);
                          e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                        }}
                        onLoad={(e) => {
                          console.log('Image loaded successfully:', imageData.url);
                        }}
                      />
                      <Box 
                        className="image-error-box"
                        sx={{
                          display: 'none',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#F5F5F5',
                          color: '#999',
                          flexDirection: 'column',
                          gap: 1
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                        <Typography variant="caption" sx={{ textAlign: 'center', px: 2 }}>
                          Image non disponible
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.7, px: 2, wordBreak: 'break-all' }}>
                          {imageData.url?.substring(0, 50)}...
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Image info overlay */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                      p: 1.5,
                      color: 'white'
                    }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                        {imageData.sequenceTitle}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                        Bloc {imageData.blockIndex + 1} - Image {imageData.imageIndex + 1}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseThumbnailDialog}
            variant="outlined"
            disabled={isSettingThumbnail}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Music Selection Dialog */}
      <Dialog
        open={musicSelectionDialogOpen}
        onClose={handleCloseMusicSelectionDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MusicNoteIcon />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Sélectionner une musique
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
          {musicsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableMusics.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <MusicNoteIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucune musique disponible
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
                Ajoutez des musiques depuis la galerie pour pouvoir les utiliser
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {availableMusics.map((music) => (
                <Grid item xs={12} sm={6} md={4} key={music.id}>
                  <Card
                    onClick={() => handleSelectMusic(music)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '2px solid #e5e7eb',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#4A90E2',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(74, 144, 226, 0.3)'
                      }
                    }}
                  >
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <MusicNoteIcon sx={{ fontSize: '2rem', color: '#4A90E2' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: '#2C3E50',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {music.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'white' }}>
                      <audio 
                        controls 
                        style={{ width: '100%' }}
                        src={music.url}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Votre navigateur ne supporte pas l'élément audio.
                      </audio>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseMusicSelectionDialog}
            variant="outlined"
            disabled={isSavingBlock}
            sx={{
              borderRadius: '20px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </div>
  );
}

// Helper function to get block type colors
function getBlockTypeColor(type) {
  switch (type) {
    case 'image':
      return { bg: '#E3F2FD', border: '#BBDEFB', text: '#1976d2' };
    case 'teacher_notes':
      return { bg: '#E8F5E8', border: '#C8E6C9', text: '#388e3c' };
    case 'quiz':
      return { bg: '#FFF3E0', border: '#FFCC02', text: '#F57C00' };
    case 'sondage':
      return { bg: '#F3E5F5', border: '#CE93D8', text: '#9C27B0' };
    case 'music':
      return { bg: '#E3F2FD', border: '#90CAF9', text: '#4A90E2' };
    default:
      return { bg: '#F5F5F5', border: '#E0E0E0', text: '#757575' };
  }
}

export default SeanceDetailsScreen;